import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { fetchWithLocalCache } from "../lib/cache";
import {
  ResponsiveContainer,
  CartesianGrid,
  Tooltip,
  Legend,
  XAxis,
  YAxis,
  Bar,
  BarChart,
  Cell,
  ReferenceLine,
  LabelList,
} from "recharts";

/* -------------------- constants -------------------- */
const MACRO_CANDIDATE_URLS = [
  "https://raw.githubusercontent.com/aport550/majorah-data/main/public/data/macro_dimension_scores.json",
  "https://cdn.jsdelivr.net/gh/aport550/majorah-data@main/public/data/macro_dimension_scores.json",
];

const DAILY_RETURNS_URL =
  "https://raw.githubusercontent.com/aport550/majorah-data/main/data/daily_returns.csv";

// Explicit fallback source for header price + chart anchors.
// Search for stockScreener in this file to verify this patch is present.
const STOCK_SCREENER_URL =
  "https://raw.githubusercontent.com/aport550/majorah-data/main/public/data/stock_screener.json";

const DECOMP_INDEX_URL =
  "https://raw.githubusercontent.com/aport550/majorah-data/main/public/data/return_decomposition_index.json";

const DECOMP_SHARDS_BASE_URL =
  "https://raw.githubusercontent.com/aport550/majorah-data/main/public/data/return_decomposition_shards";

const CLUSTERS_URL =
  "https://raw.githubusercontent.com/aport550/majorah-data/main/public/data/clusters.json";

const CORRELATION_MATRIX_URL =
  "https://raw.githubusercontent.com/aport550/majorah-data/main/data/correlation_matrix.csv";

/* -------------------- helpers -------------------- */
function isFiniteNumber(v) {
  return typeof v === "number" && Number.isFinite(v);
}

function normalizeTickerSymbol(v) {
  return String(v || "")
    .trim()
    .toUpperCase();
}

function normalizeDateString(value) {
  const s = String(value || "").trim();
  if (!s) return "";

  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;

  const parsed = new Date(s);
  if (Number.isNaN(parsed.getTime())) return s;

  const y = parsed.getFullYear();
  const m = String(parsed.getMonth() + 1).padStart(2, "0");
  const d = String(parsed.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function formatShortDate(dateStr) {
  const s = normalizeDateString(dateStr);
  if (!s) return "";
  const d = new Date(`${s}T00:00:00`);
  if (Number.isNaN(d.getTime())) return s;
  return d.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
  });
}

function formatMonthShort(dateStr) {
  const s = normalizeDateString(dateStr);
  if (!s) return "";
  const d = new Date(`${s}T00:00:00`);
  if (Number.isNaN(d.getTime())) return s;
  return d.toLocaleDateString(undefined, {
    month: "short",
    year: "2-digit",
  });
}

function formatMonthKey(dateStr) {
  const s = normalizeDateString(dateStr);
  if (!s) return "";
  return s.slice(0, 7);
}

function clamp(x, min, max) {
  return Math.max(min, Math.min(max, x));
}

function formatSignedPercentFromDecimal(v, decimals = 2) {
  if (!isFiniteNumber(v)) return "—";
  const pct = (v * 100).toFixed(decimals) + "%";
  return v > 0 ? `+${pct}` : pct;
}

function formatPercentFromDecimal(v, decimals = 2) {
  if (!isFiniteNumber(v)) return "—";
  return `${(v * 100).toFixed(decimals)}%`;
}

function formatPercentValue(v, decimals = 1) {
  if (!isFiniteNumber(v)) return "—";
  return `${v.toFixed(decimals)}%`;
}

function formatSignedPercentValue(v, decimals = 2) {
  if (!isFiniteNumber(v)) return "—";
  return `${v > 0 ? "+" : ""}${v.toFixed(decimals)}%`;
}

function formatTinyPercentValue(v, decimals = 1) {
  if (!isFiniteNumber(v)) return "";
  return `${v > 0 ? "+" : ""}${v.toFixed(decimals)}%`;
}

function formatPrice(v) {
  if (!isFiniteNumber(v)) return "—";
  return `$${v.toFixed(2)}`;
}

const tableThStyle = {
  textAlign: "left",
  padding: "12px 14px",
  fontSize: 13,
  color: "#374151",
  borderBottom: "1px solid #e5e7eb",
  whiteSpace: "nowrap",
  verticalAlign: "top",
};

const tableTdStyle = {
  padding: "12px 14px",
  borderBottom: "1px solid #f3f4f6",
  fontSize: 13,
  whiteSpace: "nowrap",
  verticalAlign: "top",
};

function MetricItem({ label, value, valueStyle }) {
  return (
    <div
      style={{
        border: "1px solid #eee",
        borderRadius: 10,
        padding: "8px 10px",
        background: "#fafafa",
      }}
    >
      <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 2 }}>
        {label}
      </div>
      <div
        style={{
          fontSize: 14,
          fontWeight: 700,
          ...(valueStyle || {}),
        }}
      >
        {value}
      </div>
    </div>
  );
}

function parseCsvLine(line) {
  const out = [];
  let cur = "";
  let inQuotes = false;

  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    const next = line[i + 1];

    if (ch === '"') {
      if (inQuotes && next === '"') {
        cur += '"';
        i++;
      } else {
        inQuotes = !inQuotes;
      }
    } else if (ch === "," && !inQuotes) {
      out.push(cur);
      cur = "";
    } else {
      cur += ch;
    }
  }

  out.push(cur);
  return out;
}

function parseReturnValue(raw) {
  if (raw === null || raw === undefined || raw === "") return null;
  const num = Number(raw);
  if (!Number.isFinite(num)) return null;
  return num / 100;
}

function normalizeKey(k) {
  return String(k || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ")
    .replace(/[^\w %/.\-<=]/g, "");
}

function buildKeyIndex(obj) {
  const idx = new Map();
  if (!obj || typeof obj !== "object") return idx;
  for (const k of Object.keys(obj)) idx.set(normalizeKey(k), k);
  return idx;
}

function getAny(row, keyIndex, candidates) {
  for (const cand of candidates) {
    const real = keyIndex.get(normalizeKey(cand));
    if (real != null) {
      const v = row?.[real];
      if (v !== null && v !== undefined && String(v).trim() !== "") return v;
    }
  }
  return null;
}

function parseFlexibleNumber(raw) {
  if (raw === null || raw === undefined || raw === "") return null;
  if (typeof raw === "number") return Number.isFinite(raw) ? raw : null;

  const original = String(raw).trim();
  if (!original) return null;

  const cleaned = original
    .replace(/[$,%]/g, "")
    .replace(/,/g, "")
    .trim();

  if (!cleaned) return null;

  const suffixMatch = cleaned.match(/^(-?\d+(?:\.\d+)?)\s*([kmbt])$/i);
  if (suffixMatch) {
    const num = Number(suffixMatch[1]);
    const suffix = suffixMatch[2].toLowerCase();
    const multiplier =
      suffix === "k"
        ? 1e3
        : suffix === "m"
        ? 1e6
        : suffix === "b"
        ? 1e9
        : suffix === "t"
        ? 1e12
        : 1;
    return Number.isFinite(num) ? num * multiplier : null;
  }

  const num = Number(cleaned);
  return Number.isFinite(num) ? num : null;
}

function getNumericAny(row, candidates) {
  if (!row || typeof row !== "object") return null;
  const idx = buildKeyIndex(row);
  const raw = getAny(row, idx, candidates);
  return parseFlexibleNumber(raw);
}

function getRawAnyFromRows(rows, candidates) {
  const sources = Array.isArray(rows) ? rows : [];

  for (const row of sources) {
    if (!row || typeof row !== "object") continue;
    const idx = buildKeyIndex(row);
    const raw = getAny(row, idx, candidates);
    if (raw !== null && raw !== undefined && String(raw).trim() !== "") {
      return raw;
    }
  }

  return null;
}

function formatCompactDollar(raw) {
  const num = parseFlexibleNumber(raw);
  if (!isFiniteNumber(num)) {
    return raw !== null && raw !== undefined && String(raw).trim() !== ""
      ? String(raw)
      : "—";
  }

  return new Intl.NumberFormat(undefined, {
    style: "currency",
    currency: "USD",
    notation: "compact",
    maximumFractionDigits: 2,
  }).format(num);
}

function formatRatioValue(raw, decimals = 2) {
  const num = parseFlexibleNumber(raw);
  if (!isFiniteNumber(num)) {
    return raw !== null && raw !== undefined && String(raw).trim() !== ""
      ? String(raw)
      : "—";
  }
  return num.toFixed(decimals);
}

function formatPercentMetricValue(raw, decimals = 2) {
  const num = parseFlexibleNumber(raw);
  if (!isFiniteNumber(num)) {
    return raw !== null && raw !== undefined && String(raw).trim() !== ""
      ? String(raw)
      : "—";
  }

  const rawText = String(raw || "");
  const pct = !rawText.includes("%") && Math.abs(num) <= 1 ? num * 100 : num;
  return `${pct.toFixed(decimals)}%`;
}

function formatPlainPriceValue(raw) {
  const num = parseFlexibleNumber(raw);
  if (!isFiniteNumber(num)) {
    return raw !== null && raw !== undefined && String(raw).trim() !== ""
      ? String(raw)
      : "—";
  }
  return formatPrice(num);
}

function formatMetricRawValue(raw) {
  const num = parseFlexibleNumber(raw);
  if (!isFiniteNumber(num)) {
    return raw !== null && raw !== undefined && String(raw).trim() !== ""
      ? String(raw)
      : "—";
  }
  return num.toFixed(2);
}

function rowTickerMatches(row, ticker) {
  if (!row || typeof row !== "object") return false;
  const idx = buildKeyIndex(row);
  const raw = getAny(row, idx, ["ticker", "Ticker", "symbol", "Symbol"]);
  return normalizeTickerSymbol(raw) === normalizeTickerSymbol(ticker);
}

function compareValues(a, b, direction = "asc") {
  const dir = direction === "asc" ? 1 : -1;

  const aMissing =
    a === null || a === undefined || a === "" || Number.isNaN(a);
  const bMissing =
    b === null || b === undefined || b === "" || Number.isNaN(b);

  if (aMissing && bMissing) return 0;
  if (aMissing) return 1;
  if (bMissing) return -1;

  if (typeof a === "number" && typeof b === "number") {
    return (a - b) * dir;
  }

  const aDate =
    typeof a === "string" && /^\d{4}-\d{2}-\d{2}$/.test(a)
      ? new Date(a).getTime()
      : null;
  const bDate =
    typeof b === "string" && /^\d{4}-\d{2}-\d{2}$/.test(b)
      ? new Date(b).getTime()
      : null;

  if (Number.isFinite(aDate) && Number.isFinite(bDate)) {
    return (aDate - bDate) * dir;
  }

  return String(a).localeCompare(String(b)) * dir;
}

function parseDailyReturnsCsvForTicker(text, ticker) {
  const lines = String(text || "")
    .split(/\r?\n/)
    .map((x) => x.trim())
    .filter(Boolean);

  if (!lines.length) return new Map();

  const header = parseCsvLine(lines[0]);

  let dateIdx = header.findIndex(
    (h) => String(h).trim().toLowerCase() === "date"
  );

  if (dateIdx === -1 && header.length > 0) {
    const firstHeader = String(header[0] || "").trim().toLowerCase();
    if (firstHeader === "" || firstHeader === "unnamed: 0") {
      dateIdx = 0;
    }
  }

  if (dateIdx === -1) {
    throw new Error("daily_returns.csv must contain a date column.");
  }

  const upperTicker = String(ticker || "").trim().toUpperCase();
  const spyIdx = header.findIndex(
    (h) => String(h || "").trim().toUpperCase() === "SPY"
  );
  const tickerIdx = header.findIndex(
    (h) => String(h || "").trim().toUpperCase() === upperTicker
  );

  const map = new Map();

  for (let i = 1; i < lines.length; i++) {
    const cols = parseCsvLine(lines[i]);
    const date = normalizeDateString(cols[dateIdx]);
    if (!date) continue;

    map.set(date, {
      SPY: spyIdx >= 0 ? parseReturnValue(cols[spyIdx]) : null,
      TICKER: tickerIdx >= 0 ? parseReturnValue(cols[tickerIdx]) : null,
    });
  }

  return map;
}

function parseCorrelationMatrixForTicker(text, ticker) {
  const lines = String(text || "")
    .split(/\r?\n/)
    .map((x) => x.trim())
    .filter(Boolean);

  if (!lines.length) return new Map();

  const header = parseCsvLine(lines[0]);
  const normalizedTarget = normalizeTickerSymbol(ticker);

  const colTickers = header.map((h, idx) => ({
    idx,
    ticker: normalizeTickerSymbol(h),
  }));

  let targetRow = null;

  for (let i = 1; i < lines.length; i++) {
    const cols = parseCsvLine(lines[i]);
    const rowTicker = normalizeTickerSymbol(cols[0]);
    if (rowTicker === normalizedTarget) {
      targetRow = cols;
      break;
    }
  }

  if (!targetRow) return new Map();

  const out = new Map();

  for (let i = 1; i < targetRow.length; i++) {
    const colTicker = colTickers[i]?.ticker;
    if (!colTicker || colTicker === normalizedTarget) continue;

    const num = Number(targetRow[i]);
    if (Number.isFinite(num)) {
      out.set(colTicker, num);
    }
  }

  return out;
}

function coerceTickerList(value) {
  if (!Array.isArray(value)) return [];

  return value
    .map((item) => {
      if (typeof item === "string") return normalizeTickerSymbol(item);
      if (item && typeof item === "object") {
        return normalizeTickerSymbol(
          item.ticker || item.symbol || item.name || item.id
        );
      }
      return "";
    })
    .filter(Boolean);
}

function findClusterForTicker(node, targetTicker) {
  const target = normalizeTickerSymbol(targetTicker);
  if (!node) return null;

  if (Array.isArray(node)) {
    const direct = coerceTickerList(node);
    if (direct.length && direct.includes(target)) {
      return Array.from(new Set(direct));
    }

    for (const item of node) {
      const found = findClusterForTicker(item, target);
      if (found) return found;
    }

    return null;
  }

  if (typeof node === "object") {
    const candidateKeys = [
      "tickers",
      "members",
      "stocks",
      "symbols",
      "cluster",
      "names",
    ];

    for (const key of candidateKeys) {
      const list = coerceTickerList(node[key]);
      if (list.length && list.includes(target)) {
        return Array.from(new Set(list));
      }
    }

    for (const value of Object.values(node)) {
      const list = coerceTickerList(value);
      if (list.length && list.includes(target)) {
        return Array.from(new Set(list));
      }
    }

    for (const value of Object.values(node)) {
      const found = findClusterForTicker(value, target);
      if (found) return found;
    }
  }

  return null;
}

async function fetchText(url) {
  const res = await fetch(url, { cache: "no-store" });
  if (!res.ok) {
    throw new Error(`HTTP ${res.status} from ${url}`);
  }
  return res.text();
}

async function fetchJson(url) {
  const res = await fetch(url, { cache: "no-store" });
  const text = await res.text();

  if (!res.ok) {
    throw new Error(`HTTP ${res.status} from ${url}`);
  }

  const trimmed = text.trim();
  if (
    !trimmed ||
    trimmed.startsWith("<!DOCTYPE") ||
    trimmed.startsWith("<html") ||
    trimmed.startsWith("<")
  ) {
    throw new Error(`Expected JSON but received non-JSON from ${url}`);
  }

  return JSON.parse(text);
}

async function fetchJsonFromCandidates(urls) {
  let lastError = null;

  for (const url of urls) {
    try {
      const res = await fetch(url, { cache: "no-store" });

      if (!res.ok) {
        lastError = new Error(`HTTP ${res.status} from ${url}`);
        continue;
      }

      const text = await res.text();
      const trimmed = text.trim();

      if (!trimmed) {
        lastError = new Error(`Empty response from ${url}`);
        continue;
      }

      if (
        trimmed.startsWith("<!DOCTYPE") ||
        trimmed.startsWith("<html") ||
        trimmed.startsWith("<")
      ) {
        lastError = new Error(`Expected JSON but received HTML from ${url}`);
        continue;
      }

      const json = JSON.parse(text);
      return { json, url };
    } catch (err) {
      lastError = err;
    }
  }

  throw lastError || new Error("Unable to load JSON.");
}

/* -------------------- macro helpers -------------------- */
function getSignLabel(v) {
  if (!isFiniteNumber(v)) return null;
  return v >= 0 ? "pos" : "neg";
}

function getMacroRegime(row) {
  const inf = getSignLabel(row?.inflation_score);
  const gro = getSignLabel(row?.growth_score);
  const liq = getSignLabel(row?.liquidity_score);

  if (!inf || !gro || !liq) return "—";

  const key = `${inf}_${gro}_${liq}`;

  const regimeMap = {
    pos_pos_pos: "Inflationary Growth + Easy Liquidity",
    pos_pos_neg: "Inflationary Growth",
    pos_neg_pos: "Stagflation + Easy Liquidity",
    pos_neg_neg: "Stagflation",
    neg_pos_pos: "Deflationary Growth",
    neg_pos_neg: "Deflationary Growth + Tight Liquidity",
    neg_neg_pos: "Deflationary Recession",
    neg_neg_neg: "Deflationary Recession + Tight Liquidity",
  };

  return regimeMap[key] || "—";
}

function getRegimeColors(regime) {
  if (!regime || regime === "—") {
    return {
      text: "#6b7280",
      bg: "#f3f4f6",
    };
  }

  const lower = regime.toLowerCase();

  if (lower.includes("stagflation")) {
    return { text: "#b45309", bg: "#fffbeb" };
  }
  if (lower.includes("deflationary recession")) {
    return { text: "#1d4ed8", bg: "#eff6ff" };
  }
  if (lower.includes("deflationary growth")) {
    return { text: "#047857", bg: "#ecfdf5" };
  }
  if (lower.includes("inflationary growth")) {
    return { text: "#7c3aed", bg: "#f5f3ff" };
  }

  return { text: "#374151", bg: "#f3f4f6" };
}

function getRegimeStyle(regime) {
  const c = getRegimeColors(regime);
  return {
    color: c.text,
    background: c.bg,
    fontWeight: 700,
    borderRadius: 9999,
    padding: "4px 10px",
    display: "inline-block",
  };
}

function getReturnStyle(v) {
  if (!isFiniteNumber(v)) return { color: "#6b7280" };
  if (v > 0) return { color: "#15803d", fontWeight: 700 };
  if (v < 0) return { color: "#b91c1c", fontWeight: 700 };
  return { color: "#374151", fontWeight: 600 };
}

function getBarColor(v) {
  if (!isFiniteNumber(v)) return "#9ca3af";
  return v >= 0 ? "#16a34a" : "#dc2626";
}

/* -------------------- news helpers -------------------- */
function toDateMs(n) {
  const d = n?.date ? Date.parse(String(n.date)) : NaN;
  if (Number.isFinite(d)) return d;

  const age = String(n?.age || "").toLowerCase().trim();
  const m = age.match(
    /(\d+)\s*(minute|min|hour|hr|day|week|month|year)s?\s*ago/
  );
  if (m) {
    const num = Number(m[1]);
    const unit = m[2];
    const mult =
      unit.startsWith("min")
        ? 60 * 1000
        : unit.startsWith("hour") || unit.startsWith("hr")
        ? 60 * 60 * 1000
        : unit.startsWith("day")
        ? 24 * 60 * 60 * 1000
        : unit.startsWith("week")
        ? 7 * 24 * 60 * 60 * 1000
        : unit.startsWith("month")
        ? 30 * 24 * 60 * 60 * 1000
        : unit.startsWith("year")
        ? 365 * 24 * 60 * 60 * 1000
        : 0;
    return Date.now() - num * mult;
  }

  return 0;
}

/* -------------------- chart tooltips -------------------- */
function SingleBarTooltip({ active, payload, label, title }) {
  if (!active || !payload || !payload.length) return null;

  const point = payload[0];

  return (
    <div
      style={{
        background: "#fff",
        border: "1px solid #e5e7eb",
        borderRadius: 10,
        padding: 10,
        boxShadow: "0 4px 16px rgba(0,0,0,0.08)",
        fontSize: 12,
      }}
    >
      <div style={{ fontWeight: 700, marginBottom: 6 }}>{label}</div>
      <div>
        {title}: {isFiniteNumber(point?.value) ? `${point.value.toFixed(2)}%` : "—"}
      </div>
    </div>
  );
}


function getMacroAxisValue(point, axisName, fallbackName) {
  const direct = point?.[axisName];
  if (isFiniteNumber(direct)) return clamp(direct, -1, 1);

  const fallback = point?.[fallbackName];
  if (isFiniteNumber(fallback)) return clamp(fallback, -1, 1);

  return 0;
}

function getMacroSideLabel(value, lowLabel, highLabel) {
  if (!isFiniteNumber(value)) return "Neutral";
  if (value > 0.15) return highLabel;
  if (value < -0.15) return lowLabel;
  return "Neutral";
}

function averageFinite(values) {
  const nums = (Array.isArray(values) ? values : []).filter(isFiniteNumber);
  if (!nums.length) return null;
  return nums.reduce((sum, v) => sum + v, 0) / nums.length;
}

function stdDevFinite(values) {
  const nums = (Array.isArray(values) ? values : []).filter(isFiniteNumber);
  if (nums.length < 2) return null;

  const avg = averageFinite(nums);
  if (!isFiniteNumber(avg)) return null;

  const variance =
    nums.reduce((sum, v) => sum + Math.pow(v - avg, 2), 0) /
    (nums.length - 1);

  return variance > 0 ? Math.sqrt(variance) : null;
}

function pearsonCorrelation(rows, xKey, yKey) {
  const pairs = (Array.isArray(rows) ? rows : [])
    .map((row) => ({ x: row?.[xKey], y: row?.[yKey] }))
    .filter((p) => isFiniteNumber(p.x) && isFiniteNumber(p.y));

  if (pairs.length < 3) return null;

  const xAvg = averageFinite(pairs.map((p) => p.x));
  const yAvg = averageFinite(pairs.map((p) => p.y));

  if (!isFiniteNumber(xAvg) || !isFiniteNumber(yAvg)) return null;

  let numerator = 0;
  let xDen = 0;
  let yDen = 0;

  for (const p of pairs) {
    const dx = p.x - xAvg;
    const dy = p.y - yAvg;
    numerator += dx * dy;
    xDen += dx * dx;
    yDen += dy * dy;
  }

  const denominator = Math.sqrt(xDen * yDen);
  if (!isFiniteNumber(denominator) || denominator === 0) return null;

  return numerator / denominator;
}

function computeMacroDimensionPreference(rows, dimensionKey) {
  const usableRows = (Array.isArray(rows) ? rows : [])
    .map((row) => ({
      dimension: row?.[dimensionKey],
      relativeReturn: isFiniteNumber(row?.relative_return)
        ? row.relative_return
        : row?.stock_return,
    }))
    .filter(
      (row) => isFiniteNumber(row.dimension) && isFiniteNumber(row.relativeReturn)
    )
    .sort((a, b) => a.dimension - b.dimension);

  if (usableRows.length < 8) {
    return {
      score: 0,
      highAvgReturn: null,
      lowAvgReturn: null,
      spread: null,
      correlation: null,
      sampleSize: usableRows.length,
    };
  }

  const bucketSize = Math.max(4, Math.floor(usableRows.length * 0.33));
  const lowRows = usableRows.slice(0, bucketSize);
  const highRows = usableRows.slice(-bucketSize);

  const lowAvgReturn = averageFinite(lowRows.map((row) => row.relativeReturn));
  const highAvgReturn = averageFinite(highRows.map((row) => row.relativeReturn));
  const spread =
    isFiniteNumber(highAvgReturn) && isFiniteNumber(lowAvgReturn)
      ? highAvgReturn - lowAvgReturn
      : null;

  const returnVol = stdDevFinite(usableRows.map((row) => row.relativeReturn));
  const spreadScore =
    isFiniteNumber(spread) && isFiniteNumber(returnVol) && returnVol > 0
      ? Math.tanh(spread / returnVol)
      : 0;

  const correlationRows = usableRows.map((row) => ({
    dimension: row.dimension,
    relativeReturn: row.relativeReturn,
  }));
  const correlation = pearsonCorrelation(
    correlationRows,
    "dimension",
    "relativeReturn"
  );

  // Blend a high-vs-low tercile spread with correlation. The spread makes the
  // output intuitive for the left/right or top/bottom chart, while correlation
  // keeps the score from being driven by one noisy bucket.
  const blendedScore = clamp(
    spreadScore * 0.7 + (isFiniteNumber(correlation) ? correlation : 0) * 0.3,
    -1,
    1
  );

  return {
    score: blendedScore,
    highAvgReturn,
    lowAvgReturn,
    spread,
    correlation,
    sampleSize: usableRows.length,
  };
}

function MacroSpectrumGauge({
  label,
  value,
  lowLabel,
  highLabel,
  accent = "#111827",
}) {
  const safeValue = isFiniteNumber(value) ? clamp(value, -1, 1) : 0;
  const markerPct = ((safeValue + 1) / 2) * 100;

  return (
    <div
      style={{
        border: "1px solid #e5e7eb",
        borderRadius: 12,
        background: "#ffffff",
        padding: "10px 12px",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "baseline",
          gap: 10,
          marginBottom: 8,
        }}
      >
        <div style={{ fontSize: 12, fontWeight: 900, color: "#111827" }}>
          {label}
        </div>
        <div style={{ fontSize: 12, fontWeight: 900, color: accent }}>
          {isFiniteNumber(value) ? safeValue.toFixed(2) : "—"}
        </div>
      </div>

      <div style={{ position: "relative", height: 30 }}>
        <div
          style={{
            position: "absolute",
            left: 0,
            right: 0,
            top: 10,
            height: 8,
            borderRadius: 999,
            background:
              "linear-gradient(90deg, #dbeafe 0%, #f3f4f6 50%, #fee2e2 100%)",
            border: "1px solid #d1d5db",
          }}
        />
        <div
          style={{
            position: "absolute",
            left: "50%",
            top: 6,
            width: 2,
            height: 16,
            background: "#111827",
            borderRadius: 999,
            transform: "translateX(-50%)",
          }}
        />
        <div
          style={{
            position: "absolute",
            left: `${markerPct}%`,
            top: 3,
            width: 18,
            height: 18,
            borderRadius: 999,
            background: accent,
            border: "3px solid #ffffff",
            boxShadow: "0 2px 8px rgba(17,24,39,0.25)",
            transform: "translateX(-50%)",
          }}
        />
      </div>

      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          fontSize: 11,
          fontWeight: 800,
          color: "#6b7280",
          marginTop: 2,
        }}
      >
        <span>{lowLabel}</span>
        <span>Neutral</span>
        <span>{highLabel}</span>
      </div>
    </div>
  );
}

function MacroCoordinateMap({ sym, data }) {
  const width = 660;
  const height = 405;
  const plotLeft = 74;
  const plotTop = 58;
  const plotWidth = 500;
  const plotHeight = 292;
  const point = data?.singlePoint || null;

  const inflationAxis = getMacroAxisValue(point, "inflationAxis", "x");
  const growthAxis = getMacroAxisValue(point, "growthAxis", "y");
  const liquidityAxis = getMacroAxisValue(point, "liquidityAxis", "z");

  const x = plotLeft + ((inflationAxis + 1) / 2) * plotWidth;
  const y = plotTop + (1 - (growthAxis + 1) / 2) * plotHeight;
  const pointRadius = 4;

  const inflationLabel = getMacroSideLabel(
    inflationAxis,
    "Low Inflation",
    "High Inflation"
  );
  const growthLabel = getMacroSideLabel(growthAxis, "Low Growth", "High Growth");
  const liquidityLabel = getMacroSideLabel(
    liquidityAxis,
    "Low Liquidity",
    "High Liquidity"
  );

  return (
    <div
      data-component-version="MACRO_COORDINATE_MAP_V5_RELATIVE_RETURN_SENSITIVITY"
      style={{
        border: "1px solid #f3f4f6",
        borderRadius: 14,
        background: "linear-gradient(180deg, #ffffff 0%, #f9fafb 100%)",
        padding: 12,
      }}
    >
      <div style={{ fontWeight: 800, fontSize: 14, marginBottom: 4 }}>
        Macro Coordinate Map — V5 Relative Return Sensitivity
      </div>
      <div
        style={{
          display: "inline-block",
          marginBottom: 8,
          padding: "3px 8px",
          borderRadius: 999,
          background: "#111827",
          color: "#ffffff",
          fontSize: 11,
          fontWeight: 900,
          letterSpacing: 0.2,
        }}
      >
        V5 · relative-return sensitivity · tiny dot · black zero axes · 3 gauges
      </div>
      <div style={{ color: "#6b7280", fontSize: 12, lineHeight: 1.35, marginBottom: 8 }}>
        One tiny plotted point only. Solid black zero axes mark neutral inflation and neutral growth. The axes now measure where the stock has historically outperformed SPY: high-score tercile returns versus low-score tercile returns, blended with correlation.
      </div>

      <div style={{ width: "100%", overflowX: "auto" }}>
        <svg
          viewBox={`0 0 ${width} ${height}`}
          width="100%"
          height="365"
          role="img"
          aria-label={`${sym} macro coordinate map`}
          style={{ minWidth: 600, display: "block" }}
        >
          <defs>
            <filter id="macroCoordinatePointShadow" x="-20%" y="-20%" width="140%" height="140%">
              <feDropShadow dx="0" dy="2" stdDeviation="2" floodOpacity="0.2" />
            </filter>
          </defs>

          <rect x="0" y="0" width={width} height={height} rx="18" fill="#ffffff" />

          <rect x={plotLeft} y={plotTop} width={plotWidth / 2} height={plotHeight / 2} fill="#ecfdf5" opacity="0.75" />
          <rect x={plotLeft + plotWidth / 2} y={plotTop} width={plotWidth / 2} height={plotHeight / 2} fill="#f5f3ff" opacity="0.75" />
          <rect x={plotLeft} y={plotTop + plotHeight / 2} width={plotWidth / 2} height={plotHeight / 2} fill="#eff6ff" opacity="0.75" />
          <rect x={plotLeft + plotWidth / 2} y={plotTop + plotHeight / 2} width={plotWidth / 2} height={plotHeight / 2} fill="#fffbeb" opacity="0.8" />

          <rect
            x={plotLeft}
            y={plotTop}
            width={plotWidth}
            height={plotHeight}
            fill="none"
            stroke="#111827"
            strokeWidth="1.25"
            rx="10"
          />

          <line
            x1={plotLeft + plotWidth / 2}
            y1={plotTop}
            x2={plotLeft + plotWidth / 2}
            y2={plotTop + plotHeight}
            stroke="#000000"
            strokeWidth="2.75"
          />
          <line
            x1={plotLeft}
            y1={plotTop + plotHeight / 2}
            x2={plotLeft + plotWidth}
            y2={plotTop + plotHeight / 2}
            stroke="#000000"
            strokeWidth="2.75"
          />

          <text x={plotLeft + 14} y={plotTop + 24} fontSize="12" fontWeight="900" fill="#047857">
            Deflationary Growth
          </text>
          <text x={plotLeft + plotWidth - 16} y={plotTop + 24} fontSize="12" fontWeight="900" fill="#7c3aed" textAnchor="end">
            Inflationary Growth
          </text>
          <text x={plotLeft + 14} y={plotTop + plotHeight - 16} fontSize="12" fontWeight="900" fill="#1d4ed8">
            Deflationary Recession
          </text>
          <text x={plotLeft + plotWidth - 16} y={plotTop + plotHeight - 16} fontSize="12" fontWeight="900" fill="#b45309" textAnchor="end">
            Stagflation
          </text>

          <text x={plotLeft} y={plotTop + plotHeight + 34} fontSize="12" fontWeight="900" fill="#dc2626">
            Low Inflation
          </text>
          <text x={plotLeft + plotWidth} y={plotTop + plotHeight + 34} fontSize="12" fontWeight="900" fill="#dc2626" textAnchor="end">
            High Inflation
          </text>
          <text x={plotLeft + plotWidth / 2} y={height - 16} fontSize="12" fontWeight="900" fill="#dc2626" textAnchor="middle">
            Inflation →
          </text>

          <text
            x="24"
            y={plotTop + 14}
            fontSize="12"
            fontWeight="900"
            fill="#2563eb"
            transform={`rotate(-90 24 ${plotTop + 14})`}
          >
            Growth →
          </text>
          <text x={plotLeft - 12} y={plotTop + 6} fontSize="12" fontWeight="900" fill="#2563eb" textAnchor="end">
            High Growth
          </text>
          <text x={plotLeft - 12} y={plotTop + plotHeight} fontSize="12" fontWeight="900" fill="#2563eb" textAnchor="end">
            Low Growth
          </text>

          {point ? (
            <g>
              <line
                x1={plotLeft + plotWidth / 2}
                y1={y}
                x2={x}
                y2={y}
                stroke="#111827"
                strokeOpacity="0.18"
                strokeDasharray="4 4"
              />
              <line
                x1={x}
                y1={plotTop + plotHeight / 2}
                x2={x}
                y2={y}
                stroke="#111827"
                strokeOpacity="0.18"
                strokeDasharray="4 4"
              />
              <circle
                cx={x}
                cy={y}
                r={pointRadius}
                fill="#111827"
                stroke="#ffffff"
                strokeWidth="3"
                filter="url(#macroCoordinatePointShadow)"
              >
                <title>
                  {sym}: {growthLabel} / {inflationLabel} / {liquidityLabel}. Avg relative return {formatSignedPercentFromDecimal(point.avg_relative_return, 2)}. Sensitivity scores: inflation {inflationAxis.toFixed(2)}, growth {growthAxis.toFixed(2)}, liquidity {liquidityAxis.toFixed(2)}.
                </title>
              </circle>
              <text
                x={x + pointRadius + 8}
                y={y - pointRadius - 4}
                fontSize="12"
                fontWeight="900"
                fill="#111827"
              >
                {sym}
              </text>
              <text
                x={x + pointRadius + 8}
                y={y + 10}
                fontSize="11"
                fontWeight="700"
                fill="#374151"
              >
                {growthLabel} / {inflationLabel}
              </text>
            </g>
          ) : null}
        </svg>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
          gap: 10,
          marginTop: 10,
        }}
      >
        <MacroSpectrumGauge
          label="Inflation Spectrum"
          value={point ? inflationAxis : null}
          lowLabel="Low Inflation"
          highLabel="High Inflation"
          accent="#dc2626"
        />
        <MacroSpectrumGauge
          label="Growth Spectrum"
          value={point ? growthAxis : null}
          lowLabel="Low Growth"
          highLabel="High Growth"
          accent="#2563eb"
        />
        <MacroSpectrumGauge
          label="Liquidity Spectrum"
          value={point ? liquidityAxis : null}
          lowLabel="Low Liquidity"
          highLabel="High Liquidity"
          accent="#7c3aed"
        />
      </div>

      <div
        style={{
          marginTop: 10,
          fontSize: 12,
          color: "#374151",
          fontWeight: 800,
        }}
      >
        {point
          ? `${sym} macro sensitivity: ${growthLabel} / ${inflationLabel} / ${liquidityLabel} · Avg relative return ${formatSignedPercentFromDecimal(point.avg_relative_return, 2)}`
          : "No macro sensitivity data available for this ticker."}
      </div>
    </div>
  );
}

const FUNDAMENTAL_METRIC_DEFS = [
  {
    label: "Market Cap",
    candidates: ["market_cap", "marketCap", "Market Cap", "MarketCap", "Market Capitalization"],
    formatter: formatCompactDollar,
  },
  {
    label: "EV",
    candidates: ["enterprise_value", "enterpriseValue", "Enterprise Value", "EV"],
    formatter: formatCompactDollar,
  },
  {
    label: "Beta",
    candidates: ["beta", "Beta"],
    formatter: formatRatioValue,
  },
  {
    label: "52 Week Low",
    candidates: ["52 Week Low", "52_week_low", "fiftyTwoWeekLow", "52w Low", "52 Week Low Price"],
    formatter: formatPlainPriceValue,
  },
  // {
  //   label: "52 Week High",
  //   candidates: ["52 Week High", "52_week_high", "fiftyTwoWeekHigh", "52w High", "52 Week High Price"],
  //   formatter: formatPlainPriceValue,
  // },
  {
    label: "RSI",
    candidates: ["rsi", "rsi14", "RSI 14", "rsi_14", "relative strength index"],
    formatter: formatMetricRawValue,
  },
  {
    label: "Dividend Yield",
    candidates: ["dividend_yield", "dividendYield", "Dividend Yield", "dividend_yield_pct", "Dividend Yield %", "trailingAnnualDividendYield"],
    formatter: formatPercentMetricValue,
  },
  {
    label: "Buyback Yield",
    candidates: ["buyback_yield", "buybackYield", "Buyback Yield", "buyback_yield_pct", "Net Buyback Yield", "shareholderYield"],
    formatter: formatPercentMetricValue,
  },
  {
    label: "Inst Ownership",
    candidates: ["inst_ownership", "institutionalOwnership", "Institutional Ownership", "Institutional Ownership %", "heldPercentInstitutions", "Institutions %"],
    formatter: formatPercentMetricValue,
  },
  {
    label: "Current Ratio",
    candidates: ["current_ratio", "currentRatio", "Current Ratio"],
    formatter: formatRatioValue,
  },
  {
    label: "P/B",
    candidates: ["price_to_book", "priceToBook", "Price To Book", "P/B", "pb", "p_b", "priceBook"],
    formatter: formatRatioValue,
  },
  {
    label: "P/TBV",
    candidates: ["price_to_tangible_book", "priceToTangibleBook", "P/TBV", "ptbv", "p_tbv", "Price/Tangible Book", "Price to Tangible Book"],
    formatter: formatRatioValue,
  },
  {
    label: "Forward PE",
    candidates: ["forward_pe", "forwardPE", "Forward P/E", "Forward PE", "fwdPE"],
    formatter: formatRatioValue,
  },
  {
    label: "PEG",
    candidates: ["peg_ratio", "pegRatio", "PEG", "PEG Ratio", "trailingPegRatio"],
    formatter: formatRatioValue,
  },
  {
    label: "Gross Margin",
    candidates: ["gross_margin", "grossMargins", "Gross Margin", "Gross Margin %", "gross_margin_pct"],
    formatter: formatPercentMetricValue,
  },
  {
    label: "Profit Margin",
    candidates: ["profit_margin", "profitMargins", "Profit Margin", "Profit Margin %", "profit_margin_pct"],
    formatter: formatPercentMetricValue,
  },
];

function FundamentalsSnapshotTable({ sym, stockScreenerRow, headerRow, asOf }) {
  const sources = [stockScreenerRow, headerRow].filter(Boolean);

  const row = FUNDAMENTAL_METRIC_DEFS.map((def) => {
    const raw = getRawAnyFromRows(sources, def.candidates);
    return {
      ...def,
      value: def.formatter(raw),
    };
  });

  return (
    <div
      style={{
        border: "1px solid #f3f4f6",
        borderRadius: 12,
        background: "#fff",
        overflow: "hidden",
        marginTop: 16,
      }}
    >
      <div
        style={{
          padding: 10,
          background: "#fafafa",
          borderBottom: "1px solid #eee",
          fontWeight: 800,
          fontSize: 14,
          display: "flex",
          gap: 10,
          alignItems: "baseline",
          flexWrap: "wrap",
        }}
      >
        <span>{sym} Fundamentals Snapshot</span>
        {asOf ? (
          <span style={{ color: "#6b7280", fontSize: 12, fontWeight: 600 }}>
            stock_screener.json as of {asOf}
          </span>
        ) : null}
      </div>

      <div style={{ overflowX: "auto" }}>
        <table
          style={{
            width: "100%",
            minWidth: 1500,
            borderCollapse: "collapse",
          }}
        >
          <thead style={{ background: "#f9fafb" }}>
            <tr>
              <th style={tableThStyle}>Ticker</th>
              {row.map((metric) => (
                <th key={metric.label} style={tableThStyle}>
                  {metric.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            <tr>
              <td style={{ ...tableTdStyle, fontWeight: 900 }}>{sym}</td>
              {row.map((metric) => (
                <td key={metric.label} style={tableTdStyle}>
                  {metric.value}
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}

function RegimeProfileBar({ rows }) {
  if (!Array.isArray(rows) || rows.length === 0) return null;

  return (
    <div style={{ marginBottom: 14 }}>
      <div
        style={{
          fontSize: 13,
          fontWeight: 800,
          color: "#111827",
          marginBottom: 8,
        }}
      >
        Macro Regime Profile
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
          gap: 8,
        }}
      >
        {rows.map((row) => {
          const colors = getRegimeColors(row.macro_regime);
          return (
            <div
              key={row.macro_regime}
              style={{
                border: `1px solid ${colors.text}22`,
                background: colors.bg,
                borderRadius: 12,
                padding: "9px 10px",
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  gap: 8,
                  alignItems: "baseline",
                }}
              >
                <div
                  style={{
                    color: colors.text,
                    fontSize: 12,
                    fontWeight: 800,
                    lineHeight: 1.2,
                  }}
                >
                  {row.macro_regime}
                </div>
                <div
                  style={{
                    color: colors.text,
                    fontSize: 16,
                    fontWeight: 900,
                    whiteSpace: "nowrap",
                  }}
                >
                  {formatPercentValue(row.profile_pct, 1)}
                </div>
              </div>

              <div
                style={{
                  height: 6,
                  background: "rgba(255,255,255,0.8)",
                  borderRadius: 999,
                  overflow: "hidden",
                  marginTop: 8,
                }}
              >
                <div
                  style={{
                    width: `${clamp(row.profile_pct || 0, 0, 100)}%`,
                    height: "100%",
                    background: colors.text,
                    borderRadius: 999,
                  }}
                />
              </div>

              <div style={{ marginTop: 6, color: "#6b7280", fontSize: 11 }}>
                Avg return {formatSignedPercentFromDecimal(row.avg_daily_return, 2)} · {row.total_days} days
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function DecompositionPanelTooltip({ active, payload, label }) {
  if (!active || !payload || !payload.length) return null;

  const point = payload[0]?.payload || {};

  return (
    <div
      style={{
        background: "#fff",
        border: "1px solid #e5e7eb",
        borderRadius: 10,
        padding: 10,
        boxShadow: "0 4px 16px rgba(0,0,0,0.08)",
        fontSize: 12,
      }}
    >
      <div style={{ fontWeight: 700, marginBottom: 6 }}>{label}</div>
      <div style={{ color: "#111827" }}>
        Total Return: {formatSignedPercentValue(point.total_return_pct)}
      </div>
      <div style={{ color: "#4f46e5" }}>
        Macro: {formatSignedPercentValue(point.macro_pct)}
      </div>
      <div style={{ color: "#2563eb" }}>
        Sector: {formatSignedPercentValue(point.cluster_pct)}
      </div>
      <div style={{ color: "#dc2626" }}>
        Idiosyncratic: {formatSignedPercentValue(point.idio_pct)}
      </div>
    </div>
  );
}

/* -------------------- labels -------------------- */
function BlackBoxBarLabel({ x, y, width, height, value }) {
  if (!isFiniteNumber(value)) return null;
  if (!isFiniteNumber(x) || !isFiniteNumber(y)) return null;
  if (!isFiniteNumber(width) || !isFiniteNumber(height)) return null;

  const label = formatTinyPercentValue(value, 1);
  if (!label) return null;

  const positive = value >= 0;
  const boxWidth = Math.max(28, label.length * 6 + 8);
  const boxHeight = 14;
  const centerX = x + width / 2;
  const rectX = centerX - boxWidth / 2;
  const rectY = positive ? y - boxHeight - 4 : y + height + 4;
  const textY = rectY + boxHeight / 2 + 0.5;

  return (
    <g>
      <rect
        x={rectX}
        y={rectY}
        width={boxWidth}
        height={boxHeight}
        rx={3}
        ry={3}
        fill="#111827"
        opacity="0.95"
      />
      <text
        x={centerX}
        y={textY}
        textAnchor="middle"
        dominantBaseline="middle"
        fontSize={9}
        fontWeight={700}
        fill="#ffffff"
      >
        {label}
      </text>
    </g>
  );
}

function PercentBarLabel({ x, y, width, value }) {
  if (
    !isFiniteNumber(value) ||
    !isFiniteNumber(x) ||
    !isFiniteNumber(y) ||
    !isFiniteNumber(width)
  ) {
    return null;
  }

  const label = `${value.toFixed(1)}%`;
  const centerX = x + width / 2;

  return (
    <g>
      <rect
        x={centerX - 20}
        y={y - 18}
        width={40}
        height={14}
        rx={3}
        ry={3}
        fill="#111827"
        opacity="0.95"
      />
      <text
        x={centerX}
        y={y - 11}
        textAnchor="middle"
        dominantBaseline="middle"
        fontSize={9}
        fontWeight={700}
        fill="#ffffff"
      >
        {label}
      </text>
    </g>
  );
}

/* -------------------- reusable reconstructed price helpers -------------------- */
function deriveCorrectedAnchorPrice(sortedRows, currentPrice, currentPriceDate) {
  if (!Array.isArray(sortedRows) || !sortedRows.length) {
    return null;
  }

  // Prefer the live/header price, but fall back to an index base of 100 so
  // the line charts still render even when the sheet/API does not return price.
  const anchorValue = isFiniteNumber(currentPrice) ? currentPrice : 100;

  const lastDailyDate = normalizeDateString(sortedRows[sortedRows.length - 1]?.date);
  const anchorDate = normalizeDateString(currentPriceDate || lastDailyDate);

  if (!lastDailyDate) return null;
  if (!anchorDate || anchorDate === lastDailyDate) return anchorValue;
  if (anchorDate < lastDailyDate) return anchorValue;

  let value = anchorValue;

  const rowsToRollback = sortedRows.filter((r) => {
    const d = normalizeDateString(r.date);
    return d > lastDailyDate && d <= anchorDate;
  });

  for (let i = rowsToRollback.length - 1; i >= 0; i--) {
    const ret = rowsToRollback[i]?.stock_return;
    if (!isFiniteNumber(ret) || 1 + ret === 0) return null;
    value = value / (1 + ret);
  }

  return value;
}

function buildReconstructedDailySeries(sortedRows, currentPrice, currentPriceDate) {
  if (!Array.isArray(sortedRows) || !sortedRows.length) {
    return [];
  }

  const correctedAnchor = deriveCorrectedAnchorPrice(sortedRows, currentPrice, currentPriceDate);
  if (!isFiniteNumber(correctedAnchor)) return [];

  const reconstructed = new Array(sortedRows.length).fill(null);
  reconstructed[sortedRows.length - 1] = correctedAnchor;

  for (let i = sortedRows.length - 2; i >= 0; i--) {
    const nextVal = reconstructed[i + 1];
    const nextRet = sortedRows[i + 1]?.stock_return;

    if (!isFiniteNumber(nextVal) || !isFiniteNumber(nextRet) || 1 + nextRet === 0) {
      reconstructed[i] = null;
    } else {
      reconstructed[i] = nextVal / (1 + nextRet);
    }
  }

  return sortedRows.map((r, i) => ({
    ...r,
    reconstructed_price: reconstructed[i],
  }));
}

/* -------------------- readable macro strip line chart -------------------- */
function MacroPerformanceStripChart({
  rows,
  sym,
  currentPrice,
  currentPriceDate,
  tickerDailyRows,
}) {
  const chartHeight = 260;
  const plotTop = 22;
  const plotHeight = 170;
  const leftAxisWidth = 54;
  const colWidth = 28;
  const gap = 4;
  const minChartWidth = 760;

  const normalizedRows = Array.isArray(rows) ? rows : [];
  const normalizedDailyRows = Array.isArray(tickerDailyRows)
    ? [...tickerDailyRows].sort((a, b) => a.date.localeCompare(b.date))
    : [];

  const lastRowDate =
    normalizedRows.length > 0
      ? normalizeDateString(normalizedRows[normalizedRows.length - 1]?.date)
      : "";

  const fallbackLatestDailyDate =
    normalizedDailyRows.length > 0
      ? normalizeDateString(normalizedDailyRows[normalizedDailyRows.length - 1]?.date)
      : "";

  const anchorDate = normalizeDateString(currentPriceDate || fallbackLatestDailyDate);

  function deriveAnchorAtLastChartDate(anchorValue, fromDate, targetDate) {
    if (!targetDate) return null;
    if (!fromDate || fromDate === targetDate) return anchorValue;

    if (fromDate < targetDate) {
      return anchorValue;
    }

    let value = anchorValue;

    const rowsToRollback = normalizedDailyRows.filter((r) => {
      const d = normalizeDateString(r.date);
      return d > targetDate && d <= fromDate;
    });

    for (let i = rowsToRollback.length - 1; i >= 0; i--) {
      const step = rowsToRollback[i];
      const retDec = step?.stock_return;

      if (!isFiniteNumber(retDec) || 1 + retDec === 0) {
        return null;
      }

      value = value / (1 + retDec);
    }

    return value;
  }

  const effectiveCurrentPrice = isFiniteNumber(currentPrice) ? currentPrice : 100;
  const usingIndexedFallback = !isFiniteNumber(currentPrice);

  const correctedAnchor = deriveAnchorAtLastChartDate(
    effectiveCurrentPrice,
    anchorDate,
    lastRowDate
  );

  const reconstructedPrices = (() => {
    if (!isFiniteNumber(correctedAnchor) || normalizedRows.length === 0) {
      return normalizedRows.map(() => null);
    }

    const closes = new Array(normalizedRows.length).fill(null);
    closes[normalizedRows.length - 1] = correctedAnchor;

    for (let i = normalizedRows.length - 2; i >= 0; i--) {
      const nextRet = normalizedRows[i + 1]?.stock_return_pct;
      const nextClose = closes[i + 1];
      const nextRetDecimal = isFiniteNumber(nextRet) ? nextRet / 100 : null;

      if (
        isFiniteNumber(nextClose) &&
        isFiniteNumber(nextRetDecimal) &&
        1 + nextRetDecimal !== 0
      ) {
        closes[i] = nextClose / (1 + nextRetDecimal);
      } else {
        closes[i] = null;
      }
    }

    return closes;
  })();

  const priceValues = reconstructedPrices.filter((v) => isFiniteNumber(v));
  const minPriceRaw = priceValues.length ? Math.min(...priceValues) : 0;
  const maxPriceRaw = priceValues.length ? Math.max(...priceValues) : 1;

  const padding = Math.max(
    (maxPriceRaw - minPriceRaw) * 0.12,
    maxPriceRaw * 0.01,
    0.5
  );
  const minPrice = minPriceRaw - padding;
  const maxPrice = maxPriceRaw + padding;
  const priceRange = maxPrice - minPrice || 1;

  const chartWidth = Math.max(
    minChartWidth,
    normalizedRows.length * colWidth + Math.max(0, normalizedRows.length - 1) * gap
  );

  const xForIndex = (i) => i * (colWidth + gap) + colWidth / 2;
  const yForPrice = (price) =>
    plotTop + ((maxPrice - price) / priceRange) * plotHeight;

  const points = normalizedRows.map((r, i) => {
    const price = reconstructedPrices[i];
    return {
      ...r,
      reconPrice: price,
      x: xForIndex(i),
      y: isFiniteNumber(price) ? yForPrice(price) : null,
    };
  });

  const validPoints = points.filter(
    (p) => isFiniteNumber(p.x) && isFiniteNumber(p.y)
  );

  const polylinePoints = validPoints.map((p) => `${p.x},${p.y}`).join(" ");

  const uniqueRegimes = Array.from(
    new Map(normalizedRows.map((r) => [r.macro_regime, r.macro_regime])).values()
  );

  const yAxisTicks = Array.from({ length: 5 }, (_, i) => {
    const value = maxPrice - (priceRange * i) / 4;
    return {
      value,
      y: yForPrice(value),
    };
  });

  const rollbackMessage =
    anchorDate &&
    lastRowDate &&
    anchorDate !== lastRowDate &&
    !usingIndexedFallback &&
    isFiniteNumber(currentPrice) &&
    isFiniteNumber(correctedAnchor)
      ? `Anchor price date ${anchorDate} was rolled back to ${lastRowDate} before plotting.`
      : "";

  return (
    <div
      style={{
        border: "1px solid #eee",
        borderRadius: 14,
        background: "#fff",
        padding: 12,
        marginBottom: 16,
      }}
    >
      <div style={{ fontWeight: 800, fontSize: 16, marginBottom: 6 }}>
        Last 30 Days: {sym} Price with Macro Regime Background
      </div>

      <div style={{ color: "#6b7280", fontSize: 13, marginBottom: 12 }}>
        Line shows reconstructed stock price using the current price as the
        anchor. If price is unavailable, it falls back to an indexed base of 100
        so the chart still renders. Background color shows the macro regime for
        each day, and the number beneath each date shows return versus SPY.
      </div>

      {rollbackMessage ? (
        <div
          style={{
            marginBottom: 10,
            fontSize: 12,
            color: "#92400e",
            background: "#fffbeb",
            border: "1px solid #fde68a",
            borderRadius: 8,
            padding: "8px 10px",
          }}
        >
          {rollbackMessage}
        </div>
      ) : null}

      {normalizedRows.length === 0 ? (
        <div style={{ padding: 18, color: "#666" }}>No chart data available.</div>
      ) : (
        <>
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: 8,
              marginBottom: 12,
            }}
          >
            {uniqueRegimes.map((regime) => {
              const c = getRegimeColors(regime);
              return (
                <div
                  key={regime}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 6,
                    fontSize: 12,
                    color: "#374151",
                  }}
                >
                  <span
                    style={{
                      width: 14,
                      height: 14,
                      borderRadius: 4,
                      background: c.bg,
                      border: `1px solid ${c.text}33`,
                      display: "inline-block",
                    }}
                  />
                  <span>{regime}</span>
                </div>
              );
            })}
          </div>

          <div style={{ overflowX: "auto", overflowY: "hidden" }}>
            <div
              style={{
                position: "relative",
                width: leftAxisWidth + chartWidth,
                height: chartHeight,
              }}
            >
              <div
                style={{
                  position: "absolute",
                  left: 0,
                  top: 0,
                  width: leftAxisWidth,
                  height: chartHeight,
                }}
              >
                {yAxisTicks.map((tick, idx) => (
                  <div
                    key={`tick-${idx}`}
                    style={{
                      position: "absolute",
                      top: tick.y - 7,
                      right: 8,
                      fontSize: 10,
                      color: "#6b7280",
                      whiteSpace: "nowrap",
                      lineHeight: 1,
                    }}
                  >
                    {formatPrice(tick.value)}
                  </div>
                ))}
              </div>

              <div
                style={{
                  position: "absolute",
                  left: leftAxisWidth,
                  top: 0,
                  width: chartWidth,
                  height: chartHeight,
                }}
              >
                {normalizedRows.map((r, i) => {
                  const c = getRegimeColors(r.macro_regime);
                  return (
                    <div
                      key={r.date}
                      style={{
                        position: "absolute",
                        left: i * (colWidth + gap),
                        top: 0,
                        width: colWidth,
                        height: plotTop + plotHeight,
                        background: c.bg,
                        border: "1px solid rgba(0,0,0,0.03)",
                        borderRadius: 4,
                      }}
                    />
                  );
                })}

                <svg
                  width={chartWidth}
                  height={chartHeight}
                  viewBox={`0 0 ${chartWidth} ${chartHeight}`}
                  style={{
                    position: "absolute",
                    inset: 0,
                    display: "block",
                    overflow: "visible",
                  }}
                >
                  {yAxisTicks.map((tick, idx) => (
                    <line
                      key={`grid-${idx}`}
                      x1="0"
                      x2={chartWidth}
                      y1={tick.y}
                      y2={tick.y}
                      stroke="#e5e7eb"
                      strokeDasharray="3 3"
                      strokeWidth="1"
                    />
                  ))}

                  <polyline
                    fill="none"
                    stroke="#111827"
                    strokeWidth="1.75"
                    strokeLinejoin="round"
                    strokeLinecap="round"
                    points={polylinePoints}
                  />

                  {validPoints.map((p) => (
                    <circle
                      key={`pt-${p.date}`}
                      cx={p.x}
                      cy={p.y}
                      r="2.5"
                      fill="#111827"
                    />
                  ))}
                </svg>

                {validPoints.map((p) => (
                  <div
                    key={`label-${p.date}`}
                    style={{
                      position: "absolute",
                      left: p.x,
                      top: Math.max(2, p.y - 16),
                      transform: "translateX(-50%)",
                      fontSize: 10,
                      lineHeight: 1,
                      color: "#374151",
                      background: "rgba(255,255,255,0.95)",
                      padding: "2px 4px",
                      borderRadius: 4,
                      whiteSpace: "nowrap",
                      pointerEvents: "none",
                    }}
                  >
                    {isFiniteNumber(p.reconPrice) ? p.reconPrice.toFixed(2) : "—"}
                  </div>
                ))}

                {normalizedRows.map((r, i) => (
                  <div
                    key={`bottom-${r.date}`}
                    style={{
                      position: "absolute",
                      left: i * (colWidth + gap),
                      top: plotTop + plotHeight + 8,
                      width: colWidth,
                      textAlign: "center",
                      fontSize: 10,
                      color: "#374151",
                      lineHeight: 1.15,
                    }}
                  >
                    <div style={{ fontWeight: 700, marginBottom: 2 }}>
                      {formatShortDate(r.date)}
                    </div>
                    <div
                      style={{
                        color:
                          r.relative_return_pct > 0
                            ? "#15803d"
                            : r.relative_return_pct < 0
                            ? "#b91c1c"
                            : "#6b7280",
                        fontWeight: 700,
                      }}
                    >
                      {isFiniteNumber(r.relative_return_pct)
                        ? `${r.relative_return_pct > 0 ? "+" : ""}${r.relative_return_pct.toFixed(1)}%`
                        : "—"}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div style={{ marginTop: 8, fontSize: 12, color: "#6b7280" }}>
            Y-axis shows {usingIndexedFallback ? "indexed value" : "reconstructed stock price"}.
          </div>
        </>
      )}
    </div>
  );
}

/* -------------------- 3-year monthly macro price chart -------------------- */
function buildMonthlyMacroPriceSeries(dailyRows, currentPrice, currentPriceDate) {
  const sortedRows = Array.isArray(dailyRows)
    ? [...dailyRows].sort((a, b) => a.date.localeCompare(b.date))
    : [];

  if (!sortedRows.length) return [];

  const reconstructedDaily = buildReconstructedDailySeries(
    sortedRows,
    currentPrice,
    currentPriceDate
  );

  if (!reconstructedDaily.length) return [];

  const monthMap = new Map();

  for (const row of reconstructedDaily) {
    const month = formatMonthKey(row.date);
    if (!month) continue;

    if (!monthMap.has(month)) {
      monthMap.set(month, {
        month,
        lastDate: row.date,
        close: row.reconstructed_price,
        inflationValues: [],
        growthValues: [],
        liquidityValues: [],
      });
    }

    const existing = monthMap.get(month);

    if (row.date >= existing.lastDate) {
      existing.lastDate = row.date;
      existing.close = row.reconstructed_price;
    }

    if (isFiniteNumber(row.inflation_score)) existing.inflationValues.push(row.inflation_score);
    if (isFiniteNumber(row.growth_score)) existing.growthValues.push(row.growth_score);
    if (isFiniteNumber(row.liquidity_score)) existing.liquidityValues.push(row.liquidity_score);
  }

  const monthly = [...monthMap.values()]
    .sort((a, b) => a.month.localeCompare(b.month))
    .map((m) => {
      const avgInflation =
        m.inflationValues.length > 0
          ? m.inflationValues.reduce((s, v) => s + v, 0) / m.inflationValues.length
          : null;
      const avgGrowth =
        m.growthValues.length > 0
          ? m.growthValues.reduce((s, v) => s + v, 0) / m.growthValues.length
          : null;
      const avgLiquidity =
        m.liquidityValues.length > 0
          ? m.liquidityValues.reduce((s, v) => s + v, 0) / m.liquidityValues.length
          : null;

      return {
        month: m.month,
        date: `${m.month}-01`,
        label: formatMonthShort(`${m.month}-01`),
        close: m.close,
        inflation_score: avgInflation,
        growth_score: avgGrowth,
        liquidity_score: avgLiquidity,
        macro_regime:
          isFiniteNumber(avgInflation) &&
          isFiniteNumber(avgGrowth) &&
          isFiniteNumber(avgLiquidity)
            ? getMacroRegime({
                inflation_score: avgInflation,
                growth_score: avgGrowth,
                liquidity_score: avgLiquidity,
              })
            : "—",
      };
    });

  return monthly.slice(-36);
}

function MonthlyMacroPriceChart({ sym, monthlyRows, usingIndexedFallback }) {
  const chartHeight = 310;
  const plotTop = 22;
  const plotHeight = 205;
  const leftAxisWidth = 58;
  const colWidth = 34;
  const gap = 4;
  const minChartWidth = 1100;

  const rows = Array.isArray(monthlyRows) ? monthlyRows : [];
  const prices = rows.map((r) => r.close).filter(isFiniteNumber);

  if (!rows.length || !prices.length) {
    return (
      <div
        style={{
          border: "1px solid #eee",
          borderRadius: 14,
          background: "#fff",
          padding: 12,
          marginBottom: 16,
        }}
      >
        <div style={{ fontWeight: 800, fontSize: 16, marginBottom: 6 }}>
          3 Year Monthly Price Chart
        </div>
        <div style={{ color: "#666" }}>No 3-year monthly price data available.</div>
      </div>
    );
  }

  const minPriceRaw = Math.min(...prices);
  const maxPriceRaw = Math.max(...prices);
  const padding = Math.max((maxPriceRaw - minPriceRaw) * 0.12, maxPriceRaw * 0.01, 0.75);
  const minPrice = minPriceRaw - padding;
  const maxPrice = maxPriceRaw + padding;
  const priceRange = maxPrice - minPrice || 1;

  const chartWidth = Math.max(
    minChartWidth,
    rows.length * colWidth + Math.max(0, rows.length - 1) * gap
  );

  const xForIndex = (i) => i * (colWidth + gap) + colWidth / 2;
  const yForPrice = (price) => plotTop + ((maxPrice - price) / priceRange) * plotHeight;

  const points = rows.map((r, i) => ({
    ...r,
    x: xForIndex(i),
    y: isFiniteNumber(r.close) ? yForPrice(r.close) : null,
  }));

  const validPoints = points.filter((p) => isFiniteNumber(p.x) && isFiniteNumber(p.y));
  const polylinePoints = validPoints.map((p) => `${p.x},${p.y}`).join(" ");

  const uniqueRegimes = Array.from(
    new Map(rows.map((r) => [r.macro_regime, r.macro_regime])).values()
  );

  const yAxisTicks = Array.from({ length: 5 }, (_, i) => {
    const value = maxPrice - (priceRange * i) / 4;
    return { value, y: yForPrice(value) };
  });

  return (
    <div
      style={{
        border: "1px solid #eee",
        borderRadius: 14,
        background: "#fff",
        padding: 12,
        marginBottom: 16,
      }}
    >
      <div style={{ fontWeight: 800, fontSize: 16, marginBottom: 6 }}>
        3 Year Monthly Price Chart
      </div>
      <div style={{ color: "#6b7280", fontSize: 13, marginBottom: 12 }}>
        Monthly reconstructed closing prices for the last 36 months. If price is
        unavailable, the series is indexed to 100. Each month’s background band
        is colored by the overall macro regime of that month.
      </div>

      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: 8,
          marginBottom: 12,
        }}
      >
        {uniqueRegimes.map((regime) => {
          const c = getRegimeColors(regime);
          return (
            <div
              key={regime}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                fontSize: 12,
                color: "#374151",
              }}
            >
              <span
                style={{
                  width: 14,
                  height: 14,
                  borderRadius: 4,
                  background: c.bg,
                  border: `1px solid ${c.text}33`,
                  display: "inline-block",
                }}
              />
              <span>{regime}</span>
            </div>
          );
        })}
      </div>

      <div style={{ overflowX: "auto", overflowY: "hidden" }}>
        <div
          style={{
            position: "relative",
            width: leftAxisWidth + chartWidth,
            height: chartHeight,
          }}
        >
          <div
            style={{
              position: "absolute",
              left: 0,
              top: 0,
              width: leftAxisWidth,
              height: chartHeight,
            }}
          >
            {yAxisTicks.map((tick, idx) => (
              <div
                key={`tick-${idx}`}
                style={{
                  position: "absolute",
                  top: tick.y - 7,
                  right: 8,
                  fontSize: 10,
                  color: "#6b7280",
                  whiteSpace: "nowrap",
                  lineHeight: 1,
                }}
              >
                {formatPrice(tick.value)}
              </div>
            ))}
          </div>

          <div
            style={{
              position: "absolute",
              left: leftAxisWidth,
              top: 0,
              width: chartWidth,
              height: chartHeight,
            }}
          >
            {rows.map((r, i) => {
              const c = getRegimeColors(r.macro_regime);
              return (
                <div
                  key={r.month}
                  style={{
                    position: "absolute",
                    left: i * (colWidth + gap),
                    top: 0,
                    width: colWidth,
                    height: plotTop + plotHeight,
                    background: c.bg,
                    border: "1px solid rgba(0,0,0,0.03)",
                    borderRadius: 4,
                  }}
                />
              );
            })}

            <svg
              width={chartWidth}
              height={chartHeight}
              viewBox={`0 0 ${chartWidth} ${chartHeight}`}
              style={{
                position: "absolute",
                inset: 0,
                display: "block",
                overflow: "visible",
              }}
            >
              {yAxisTicks.map((tick, idx) => (
                <line
                  key={`grid-${idx}`}
                  x1="0"
                  x2={chartWidth}
                  y1={tick.y}
                  y2={tick.y}
                  stroke="#e5e7eb"
                  strokeDasharray="3 3"
                  strokeWidth="1"
                />
              ))}

              <polyline
                fill="none"
                stroke="#111827"
                strokeWidth="2"
                strokeLinejoin="round"
                strokeLinecap="round"
                points={polylinePoints}
              />

              {validPoints.map((p) => (
                <circle
                  key={`pt-${p.month}`}
                  cx={p.x}
                  cy={p.y}
                  r="2.5"
                  fill="#111827"
                />
              ))}
            </svg>

            {validPoints.map((p) => (
              <div
                key={`label-${p.month}`}
                style={{
                  position: "absolute",
                  left: p.x,
                  top: Math.max(2, p.y - 16),
                  transform: "translateX(-50%)",
                  fontSize: 10,
                  lineHeight: 1,
                  color: "#374151",
                  background: "rgba(255,255,255,0.96)",
                  padding: "2px 4px",
                  borderRadius: 4,
                  whiteSpace: "nowrap",
                  pointerEvents: "none",
                }}
              >
                {isFiniteNumber(p.close) ? p.close.toFixed(2) : "—"}
              </div>
            ))}

            {rows.map((r, i) => (
              <div
                key={`bottom-${r.month}`}
                style={{
                  position: "absolute",
                  left: i * (colWidth + gap),
                  top: plotTop + plotHeight + 8,
                  width: colWidth,
                  textAlign: "center",
                  fontSize: 10,
                  color: "#374151",
                  lineHeight: 1.15,
                }}
              >
                <div style={{ fontWeight: 700 }}>
                  {formatMonthShort(r.date).split(" ")[0]}
                </div>
                <div style={{ color: "#6b7280" }}>
                  {formatMonthShort(r.date).split(" ")[1]}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div style={{ marginTop: 8, fontSize: 12, color: "#6b7280" }}>
        Y-axis shows {usingIndexedFallback ? "indexed value" : "reconstructed monthly closing price"}.
      </div>
    </div>
  );
}

/* -------------------- volatility + range -------------------- */
function buildVolatilityStats(dailyRows, currentPrice, currentPriceDate) {
  if (
    !Array.isArray(dailyRows) ||
    dailyRows.length === 0 ||
    !isFiniteNumber(currentPrice)
  ) {
    return null;
  }

  const sortedRows = [...dailyRows].sort((a, b) => a.date.localeCompare(b.date));
  const reconstructedSeries = buildReconstructedDailySeries(
    sortedRows,
    currentPrice,
    currentPriceDate
  );

  if (!reconstructedSeries.length) return null;

  const bucketDefs = [
    { label: "Down -3%+", test: (v) => v < -0.03 },
    { label: "Down -2% to -3%", test: (v) => v < -0.02 && v >= -0.03 },
    { label: "Down -1% to -2%", test: (v) => v < -0.01 && v >= -0.02 },
    { label: "Down 0% to -1%", test: (v) => v < 0 && v >= -0.01 },
    { label: "Up 0% to 1%", test: (v) => v >= 0 && v < 0.01 },
    { label: "Up 1% to 2%", test: (v) => v >= 0.01 && v < 0.02 },
    { label: "Up 2% to 3%", test: (v) => v >= 0.02 && v < 0.03 },
    { label: "Up 3%+", test: (v) => v >= 0.03 },
  ];

  const totalValidDays = sortedRows.filter((r) =>
    isFiniteNumber(r.stock_return)
  ).length;

  const buckets = bucketDefs.map((bucket) => {
    const count = sortedRows.filter((r) => bucket.test(r.stock_return)).length;
    const pctOfDays = totalValidDays > 0 ? count / totalValidDays : null;
    return {
      label: bucket.label,
      count,
      pctOfDays,
      pctOfDaysPct: isFiniteNumber(pctOfDays) ? pctOfDays * 100 : null,
    };
  });

  const series = reconstructedSeries.map((r) => ({
    date: r.date,
    price: r.reconstructed_price,
  }));

  function computeRange(tradingDays) {
    const slice = series.slice(-tradingDays);
    const prices = slice.map((r) => r.price).filter(isFiniteNumber);
    if (!prices.length) return null;

    const low = Math.min(...prices);
    const high = Math.max(...prices);

    return {
      low,
      high,
      pctDiff: low !== 0 ? (high - low) / low : null,
    };
  }

  return {
    buckets,
    range8w: computeRange(40),
    range52w: computeRange(252),
    range104w: computeRange(504),
  };
}

function VolatilityRangeSection({ sym, stats }) {
  if (!stats) {
    return (
      <div
        style={{
          border: "1px solid #eee",
          borderRadius: 14,
          background: "#fff",
          padding: 12,
          marginBottom: 16,
        }}
      >
        <div style={{ fontWeight: 800, fontSize: 16, marginBottom: 6 }}>
          {sym} Volatility & Range
        </div>
        <div style={{ color: "#666" }}>No volatility data available.</div>
      </div>
    );
  }

  const rangeRows = [
    { label: "8 Week Range", data: stats.range8w },
    { label: "52 Week Range", data: stats.range52w },
    { label: "104 Week Range", data: stats.range104w },
  ];

  return (
    <div
      style={{
        border: "1px solid #eee",
        borderRadius: 14,
        background: "#fff",
        padding: 12,
        marginBottom: 16,
      }}
    >
      <div style={{ fontWeight: 800, fontSize: 16, marginBottom: 6 }}>
        {sym} Volatility & Range
      </div>
      <div style={{ color: "#6b7280", fontSize: 13, marginBottom: 12 }}>
        Return-category distribution on top, with reconstructed price ranges
        over the last 8, 52, and 104 weeks below.
      </div>

      <div style={{ width: "100%", height: 340, marginBottom: 14 }}>
        <ResponsiveContainer>
          <BarChart
            data={stats.buckets}
            margin={{ top: 24, right: 20, left: 0, bottom: 40 }}
          >
            <CartesianGrid strokeDasharray="3 3" vertical={false} />
            <XAxis
              dataKey="label"
              interval={0}
              angle={-25}
              textAnchor="end"
              height={70}
              tick={{ fontSize: 11 }}
            />
            <YAxis
              tickFormatter={(v) => `${v.toFixed(0)}%`}
              domain={[0, "auto"]}
            />
            <Tooltip content={<SingleBarTooltip title="% of Days" />} />
            <Legend />
            <Bar
              dataKey="pctOfDaysPct"
              name="% of Days"
              fill="#2563eb"
              radius={[4, 4, 0, 0]}
            >
              <LabelList dataKey="pctOfDaysPct" content={<PercentBarLabel />} />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      <div style={{ overflowX: "auto" }}>
        <table
          style={{
            width: "100%",
            minWidth: 620,
            borderCollapse: "collapse",
          }}
        >
          <thead style={{ background: "#f9fafb" }}>
            <tr>
              <th style={tableThStyle}>Range Window</th>
              <th style={tableThStyle}>Low</th>
              <th style={tableThStyle}>High</th>
              <th style={tableThStyle}>Pct Diff</th>
            </tr>
          </thead>
          <tbody>
            {rangeRows.map((row) => (
              <tr key={row.label}>
                <td style={tableTdStyle}>{row.label}</td>
                <td style={tableTdStyle}>
                  {row.data ? formatPrice(row.data.low) : "—"}
                </td>
                <td style={tableTdStyle}>
                  {row.data ? formatPrice(row.data.high) : "—"}
                </td>
                <td style={tableTdStyle}>
                  {row.data && isFiniteNumber(row.data.pctDiff)
                    ? formatPercentFromDecimal(row.data.pctDiff)
                    : "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* -------------------- asymmetry -------------------- */
function cumulativeReturnDecimal(returns) {
  if (!Array.isArray(returns) || !returns.length) return null;

  let product = 1;
  let used = 0;

  for (const r of returns) {
    if (!isFiniteNumber(r)) return null;
    product *= 1 + r;
    used += 1;
  }

  return used ? product - 1 : null;
}

function computeAsymmetryStats(
  dailyRows,
  {
    dropThreshold = -0.03,
    lookaheadDays = 5,
    minEvents = 1,
  } = {}
) {
  if (!Array.isArray(dailyRows) || dailyRows.length < lookaheadDays + 2) {
    return null;
  }

  const sorted = [...dailyRows].sort((a, b) => a.date.localeCompare(b.date));

  const eventOutcomes = [];

  for (let i = 0; i < sorted.length - lookaheadDays; i++) {
    const eventReturn = sorted[i]?.stock_return;

    if (!isFiniteNumber(eventReturn)) continue;
    if (eventReturn > dropThreshold) continue;

    const forwardWindow = sorted
      .slice(i + 1, i + 1 + lookaheadDays)
      .map((r) => r?.stock_return);

    if (forwardWindow.length < lookaheadDays) continue;
    if (forwardWindow.some((r) => !isFiniteNumber(r))) continue;

    const forwardReturn = cumulativeReturnDecimal(forwardWindow);
    if (!isFiniteNumber(forwardReturn)) continue;

    eventOutcomes.push({
      eventDate: sorted[i].date,
      eventReturn,
      forwardReturn,
    });
  }

  const totalEvents = eventOutcomes.length;

  const lastRow = sorted[sorted.length - 1];
  const latestDailyReturn = isFiniteNumber(lastRow?.stock_return)
    ? lastRow.stock_return
    : null;

  const currentTriggerActive =
    isFiniteNumber(latestDailyReturn) && latestDailyReturn <= dropThreshold;

  if (totalEvents < minEvents) {
    return {
      totalEvents,
      currentTriggerActive,
      latestDailyReturn,
      avgForwardReturn: null,
      winRate: null,
      avgPositiveForwardReturn: null,
      avgNegativeForwardReturn: null,
      asymmetryRatio: null,
      expectancy: null,
      score: null,
      outcomes: eventOutcomes,
    };
  }

  const positive = eventOutcomes.filter((x) => x.forwardReturn > 0);
  const negative = eventOutcomes.filter((x) => x.forwardReturn < 0);

  const avgForwardReturn =
    eventOutcomes.reduce((sum, x) => sum + x.forwardReturn, 0) / totalEvents;

  const winRate = positive.length / totalEvents;

  const avgPositiveForwardReturn =
    positive.length > 0
      ? positive.reduce((sum, x) => sum + x.forwardReturn, 0) / positive.length
      : null;

  const avgNegativeForwardReturn =
    negative.length > 0
      ? negative.reduce((sum, x) => sum + Math.abs(x.forwardReturn), 0) / negative.length
      : null;

  const weightedUpside =
    isFiniteNumber(avgPositiveForwardReturn) ? winRate * avgPositiveForwardReturn : 0;

  const weightedDownside =
    isFiniteNumber(avgNegativeForwardReturn)
      ? (1 - winRate) * avgNegativeForwardReturn
      : 0;

  const asymmetryRatio =
    weightedDownside > 0 ? weightedUpside / weightedDownside : null;

  const expectancy = weightedUpside - weightedDownside;

  const scoreFromExpectancy = isFiniteNumber(expectancy)
    ? 50 + expectancy * 1000
    : null;

  const scoreFromRatio = isFiniteNumber(asymmetryRatio)
    ? 50 + (asymmetryRatio - 1) * 20
    : null;

  const rawScore =
    isFiniteNumber(scoreFromExpectancy) && isFiniteNumber(scoreFromRatio)
      ? scoreFromExpectancy * 0.6 + scoreFromRatio * 0.4
      : scoreFromExpectancy ?? scoreFromRatio;

  const score = isFiniteNumber(rawScore) ? clamp(rawScore, 0, 100) : null;

  return {
    totalEvents,
    currentTriggerActive,
    latestDailyReturn,
    avgForwardReturn,
    winRate,
    avgPositiveForwardReturn,
    avgNegativeForwardReturn,
    asymmetryRatio,
    expectancy,
    score,
    outcomes: eventOutcomes,
  };
}

function AsymmetrySection({
  sym,
  stats,
  lookaheadDays = 5,
  dropThreshold = -0.03,
}) {
  if (!stats) {
    return (
      <div
        style={{
          border: "1px solid #eee",
          borderRadius: 14,
          background: "#fff",
          padding: 12,
          marginBottom: 16,
        }}
      >
        <div style={{ fontWeight: 800, fontSize: 16, marginBottom: 6 }}>
          {sym} Crash-Bounce Asymmetry
        </div>
        <div style={{ color: "#666" }}>No asymmetry data available.</div>
      </div>
    );
  }

  const thresholdText = `${Math.abs(dropThreshold * 100).toFixed(0)}%`;

  return (
    <div
      style={{
        border: "1px solid #eee",
        borderRadius: 14,
        background: "#fff",
        padding: 12,
        marginBottom: 16,
      }}
    >
      <div style={{ fontWeight: 800, fontSize: 16, marginBottom: 6 }}>
        {sym} Crash-Bounce Asymmetry
      </div>

      <div style={{ color: "#6b7280", fontSize: 13, marginBottom: 12 }}>
        Looks at historical {thresholdText}+ 1-day drops and measures how the stock
        behaved over the next {lookaheadDays} trading days. High scores mean the stock
        has historically bounced more than it continued falling after crash-like days.
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
          gap: 10,
          marginBottom: 14,
        }}
      >
        <MetricItem
          label="Asymmetry Score"
          value={isFiniteNumber(stats.score) ? `${stats.score.toFixed(1)} / 100` : "—"}
        />
        <MetricItem
          label="Current Setup Active?"
          value={stats.currentTriggerActive ? "Yes" : "No"}
          valueStyle={{
            color: stats.currentTriggerActive ? "#15803d" : "#6b7280",
            fontWeight: 800,
          }}
        />
        <MetricItem
          label="Latest Daily Return"
          value={formatSignedPercentFromDecimal(stats.latestDailyReturn, 2)}
          valueStyle={getReturnStyle(stats.latestDailyReturn)}
        />
        <MetricItem
          label="Historical Crash Events"
          value={String(stats.totalEvents ?? 0)}
        />
        <MetricItem
          label={`Avg ${lookaheadDays}D Forward Return`}
          value={formatSignedPercentFromDecimal(stats.avgForwardReturn, 2)}
          valueStyle={getReturnStyle(stats.avgForwardReturn)}
        />
        <MetricItem
          label="Win Rate After Crash"
          value={formatPercentFromDecimal(stats.winRate, 1)}
        />
        <MetricItem
          label="Avg Positive Forward Return"
          value={formatSignedPercentFromDecimal(stats.avgPositiveForwardReturn, 2)}
          valueStyle={getReturnStyle(stats.avgPositiveForwardReturn)}
        />
        <MetricItem
          label="Avg Negative Forward Return"
          value={
            isFiniteNumber(stats.avgNegativeForwardReturn)
              ? `-${(stats.avgNegativeForwardReturn * 100).toFixed(2)}%`
              : "—"
          }
          valueStyle={{
            color: isFiniteNumber(stats.avgNegativeForwardReturn) ? "#b91c1c" : "#6b7280",
            fontWeight: 700,
          }}
        />
        <MetricItem
          label="Asymmetry Ratio"
          value={isFiniteNumber(stats.asymmetryRatio) ? stats.asymmetryRatio.toFixed(2) : "—"}
        />
        <MetricItem
          label="Expectancy"
          value={formatSignedPercentFromDecimal(stats.expectancy, 2)}
          valueStyle={getReturnStyle(stats.expectancy)}
        />
      </div>

      <div style={{ overflowX: "auto" }}>
        <table
          style={{
            width: "100%",
            minWidth: 560,
            borderCollapse: "collapse",
          }}
        >
          <thead style={{ background: "#f9fafb" }}>
            <tr>
              <th style={tableThStyle}>Crash Date</th>
              <th style={tableThStyle}>Crash Day Return</th>
              <th style={tableThStyle}>{lookaheadDays}D Forward Return</th>
            </tr>
          </thead>
          <tbody>
            {(stats.outcomes || []).length === 0 ? (
              <tr>
                <td style={{ ...tableTdStyle, color: "#6b7280" }} colSpan={3}>
                  No {thresholdText}+ daily drops found for this ticker in the loaded return history.
                </td>
              </tr>
            ) : (
              (stats.outcomes || []).slice(-12).reverse().map((row) => (
                <tr key={row.eventDate}>
                  <td style={tableTdStyle}>{formatShortDate(row.eventDate)}</td>
                  <td style={{ ...tableTdStyle, ...getReturnStyle(row.eventReturn) }}>
                    {formatSignedPercentFromDecimal(row.eventReturn, 2)}
                  </td>
                  <td style={{ ...tableTdStyle, ...getReturnStyle(row.forwardReturn) }}>
                    {formatSignedPercentFromDecimal(row.forwardReturn, 2)}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* -------------------- decomposition mini-panels -------------------- */
function DecompositionMiniPanel({
  title,
  dataKey,
  color,
  rows,
  domain,
  showXAxis = false,
  showDataLabels = false,
}) {
  return (
    <div style={{ width: "100%", height: showXAxis ? 150 : 130 }}>
      <div
        style={{
          fontSize: 12,
          fontWeight: 800,
          color,
          marginBottom: 4,
        }}
      >
        {title}
      </div>

      <ResponsiveContainer>
        <BarChart
          data={rows}
          margin={{ top: 18, right: 12, left: 0, bottom: showXAxis ? 12 : 0 }}
          barCategoryGap="18%"
        >
          <CartesianGrid strokeDasharray="3 3" vertical={false} />
          <XAxis
            dataKey="label"
            hide={!showXAxis}
            minTickGap={16}
            tick={{ fontSize: 11 }}
          />
          <YAxis
            domain={domain}
            tickFormatter={(v) => `${v.toFixed(1)}%`}
            width={46}
            tick={{ fontSize: 11 }}
          />
          <ReferenceLine y={0} stroke="#9ca3af" strokeWidth={1} />
          <Tooltip content={<DecompositionPanelTooltip />} />
          <Bar dataKey={dataKey} fill={color} radius={[3, 3, 0, 0]}>
            {showDataLabels ? (
              <LabelList dataKey={dataKey} content={<BlackBoxBarLabel />} />
            ) : null}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

/* -------------------- component -------------------- */
export default function StockDetail() {
  const params = useParams();
  const sym = String(params.symbol || params.ticker || params.id || "")
    .trim()
    .toUpperCase();

  const [searchParams] = useSearchParams();
  const exParam = String(searchParams.get("ex") || "").trim().toUpperCase();

  const [headerRow, setHeaderRow] = useState(null);
  const [headerError, setHeaderError] = useState("");

  const [stockScreenerRow, setStockScreenerRow] = useState(null);
  const [stockScreenerAsOf, setStockScreenerAsOf] = useState("");
  const [stockScreenerError, setStockScreenerError] = useState("");

  const [macroRows, setMacroRows] = useState([]);
  const [tickerDailyRows, setTickerDailyRows] = useState([]);
  const [loadingMacroTable, setLoadingMacroTable] = useState(true);
  const [macroTableError, setMacroTableError] = useState("");

  const [decompRows, setDecompRows] = useState([]);
  const [loadingDecomp, setLoadingDecomp] = useState(true);
  const [decompError, setDecompError] = useState("");

  const [clusterRows, setClusterRows] = useState([]);
  const [loadingCluster, setLoadingCluster] = useState(true);
  const [clusterError, setClusterError] = useState("");

  const [news, setNews] = useState([]);
  const [loadingNews, setLoadingNews] = useState(true);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        setHeaderError("");
        setHeaderRow(null);

        const base = process.env.REACT_APP_SHEETS_API_URL;
        const apiKey = process.env.REACT_APP_API_KEY;

        if (!base || !apiKey || !sym) return;

        const detailUrlTicker = `${base}?type=stocks&view=detail&ticker=${encodeURIComponent(
          sym
        )}&key=${encodeURIComponent(apiKey)}`;
        const detailUrlSymbol = `${base}?type=stocks&view=detail&symbol=${encodeURIComponent(
          sym
        )}&key=${encodeURIComponent(apiKey)}`;

        async function fetchRecord(url, cacheKey) {
          const res = await fetchWithLocalCache(url, cacheKey, 5 * 60 * 1000);
          const payload = res?.data ?? res;
          if (payload?.error) throw new Error(payload.error);
          let record = payload?.row ?? payload;
          if (Array.isArray(record)) record = record[0] ?? null;
          return record && typeof record === "object" ? record : null;
        }

        function getRecordSymbol(record) {
          if (!record || typeof record !== "object") return "";
          const idx = buildKeyIndex(record);
          const raw = getAny(record, idx, ["Symbol", "Ticker"]);
          return String(raw || "").trim().toUpperCase();
        }

        let record = await fetchRecord(
          detailUrlTicker,
          `stock_detail_${sym}_ticker`
        );

        if (!record || getRecordSymbol(record) !== sym) {
          record = await fetchRecord(
            detailUrlSymbol,
            `stock_detail_${sym}_symbol`
          );
        }

        if (!record || getRecordSymbol(record) !== sym) {
          const listUrl = `${base}?type=stocks&view=list&key=${encodeURIComponent(
            apiKey
          )}`;
          const res = await fetchWithLocalCache(
            listUrl,
            "stocks_list_for_header_v2",
            10 * 60 * 1000
          );
          const payload = res?.data ?? res;
          if (payload?.error) throw new Error(payload.error);

          const list = Array.isArray(payload) ? payload : [];
          record =
            list.find((r) => {
              const idx = buildKeyIndex(r);
              const raw = getAny(r, idx, ["Symbol", "Ticker"]);
              return String(raw || "").trim().toUpperCase() === sym;
            }) || null;
        }

        if (!cancelled) setHeaderRow(record || null);
      } catch (err) {
        if (!cancelled) {
          setHeaderError(err?.message || "Unable to load stock header.");
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [sym]);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        setStockScreenerError("");
        setStockScreenerRow(null);
        setStockScreenerAsOf("");

        if (!sym) return;

        const json = await fetchJson(STOCK_SCREENER_URL);
        const rows = Array.isArray(json?.data)
          ? json.data
          : Array.isArray(json)
          ? json
          : [];

        const found = rows.find((row) => rowTickerMatches(row, sym)) || null;

        if (!cancelled) {
          setStockScreenerRow(found);
          setStockScreenerAsOf(String(json?.as_of || json?.asOf || ""));
        }
      } catch (err) {
        if (!cancelled) {
          setStockScreenerError(
            err?.message || "Unable to load stock_screener.json price fallback."
          );
          setStockScreenerRow(null);
          setStockScreenerAsOf("");
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [sym]);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        setLoadingNews(true);
        setNews([]);

        if (!sym) return;

        const r = await fetch(
          `/api/googleNews?symbol=${encodeURIComponent(sym)}&limit=100`
        );
        const j = await r.json();
        if (!r.ok) throw new Error(j?.error || "Failed to load Google articles");

        const items = Array.isArray(j?.items) ? j.items : [];
        items.sort((a, b) => toDateMs(b) - toDateMs(a));

        if (!cancelled) setNews(items);
      } catch {
        if (!cancelled) setNews([]);
      } finally {
        if (!cancelled) setLoadingNews(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [sym]);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        setLoadingMacroTable(true);
        setMacroTableError("");
        setMacroRows([]);
        setTickerDailyRows([]);

        if (!sym) return;

        const [{ json: macroJson }, dailyReturnsText] = await Promise.all([
          fetchJsonFromCandidates(MACRO_CANDIDATE_URLS),
          fetchText(DAILY_RETURNS_URL),
        ]);

        if (!Array.isArray(macroJson)) {
          throw new Error("Macro dimension JSON is not an array.");
        }

        const returnsByDate = parseDailyReturnsCsvForTicker(
          dailyReturnsText,
          sym
        );

        const macroByDate = new Map(
          macroJson
            .map((m) => {
              const date = normalizeDateString(m?.date);
              if (!date) return null;
              return [
                date,
                {
                  inflation_score: Number.isFinite(Number(m?.inflation_score))
                    ? Number(m.inflation_score)
                    : null,
                  growth_score: Number.isFinite(Number(m?.growth_score))
                    ? Number(m.growth_score)
                    : null,
                  liquidity_score: Number.isFinite(Number(m?.liquidity_score))
                    ? Number(m.liquidity_score)
                    : null,
                },
              ];
            })
            .filter(Boolean)
        );

        const fullDailyRows = [...returnsByDate.entries()]
          .map(([date, returnRow]) => {
            const stockReturn = returnRow.TICKER ?? null;
            const spyReturn = returnRow.SPY ?? null;
            const relativeReturn =
              isFiniteNumber(stockReturn) && isFiniteNumber(spyReturn)
                ? stockReturn - spyReturn
                : null;
            const macro = macroByDate.get(date) || {};

            return {
              date,
              stock_return: stockReturn,
              spy_return: spyReturn,
              relative_return: relativeReturn,
              inflation_score: macro.inflation_score ?? null,
              growth_score: macro.growth_score ?? null,
              liquidity_score: macro.liquidity_score ?? null,
              macro_regime:
                macro.inflation_score != null &&
                macro.growth_score != null &&
                macro.liquidity_score != null
                  ? getMacroRegime(macro)
                  : "—",
            };
          })
          .sort((a, b) => a.date.localeCompare(b.date));

        const mergedMacroRows = fullDailyRows.filter(
          (r) =>
            r.date &&
            (r.stock_return !== null || r.spy_return !== null) &&
            r.inflation_score !== null &&
            r.growth_score !== null &&
            r.liquidity_score !== null
        );

        if (!cancelled) {
          setTickerDailyRows(fullDailyRows);
          setMacroRows(mergedMacroRows);
        }
      } catch (err) {
        if (!cancelled) {
          setMacroTableError(
            err?.message || "Failed to load daily return / macro table."
          );
          setMacroRows([]);
          setTickerDailyRows([]);
        }
      } finally {
        if (!cancelled) setLoadingMacroTable(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [sym]);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        setLoadingDecomp(true);
        setDecompError("");
        setDecompRows([]);

        if (!sym) return;

        const indexJson = await fetchJson(DECOMP_INDEX_URL);
        const shardFile = indexJson?.[sym];

        if (!shardFile) {
          if (!cancelled) setDecompRows([]);
          return;
        }

        const shardJson = await fetchJson(
          `${DECOMP_SHARDS_BASE_URL}/${shardFile}`
        );
        const tickerBlock = Array.isArray(shardJson)
          ? shardJson.find(
              (x) => String(x?.ticker || "").trim().toUpperCase() === sym
            )
          : null;

        const rows = Array.isArray(tickerBlock?.rows) ? tickerBlock.rows : [];

        if (!cancelled) setDecompRows(rows);
      } catch (err) {
        if (!cancelled) {
          setDecompError(
            err?.message || "Failed to load return decomposition data."
          );
          setDecompRows([]);
        }
      } finally {
        if (!cancelled) setLoadingDecomp(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [sym]);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        setLoadingCluster(true);
        setClusterError("");
        setClusterRows([]);

        if (!sym) return;

        const [clustersJson, corrText] = await Promise.all([
          fetchJson(CLUSTERS_URL),
          fetchText(CORRELATION_MATRIX_URL),
        ]);

        const clusterTickers = findClusterForTicker(clustersJson, sym) || [];
        const correlationMap = parseCorrelationMatrixForTicker(corrText, sym);

        const rows = clusterTickers
          .filter((ticker) => ticker && ticker !== sym)
          .map((ticker) => ({
            ticker,
            correlation: correlationMap.get(ticker) ?? null,
          }))
          .sort((a, b) => {
            const aVal = isFiniteNumber(a.correlation) ? a.correlation : -999;
            const bVal = isFiniteNumber(b.correlation) ? b.correlation : -999;
            if (bVal !== aVal) return bVal - aVal;
            return a.ticker.localeCompare(b.ticker);
          });

        if (!cancelled) setClusterRows(rows);
      } catch (err) {
        if (!cancelled) {
          setClusterError(
            err?.message || "Failed to load stock cluster information."
          );
          setClusterRows([]);
        }
      } finally {
        if (!cancelled) setLoadingCluster(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [sym]);

  const headerKeyIndex = useMemo(() => buildKeyIndex(headerRow), [headerRow]);

  const priceFieldCandidates = useMemo(
    () => [
      "price",
      "Price",
      "lastPrice",
      "Last Price",
      "Last",
      "Current Price",
      "CurrentPrice",
      "regularMarketPrice",
      "Regular Market Price",
      "marketPrice",
      "Market Price",
      "close",
      "Close",
      "previousClose",
      "Previous Close",
    ],
    []
  );

  const stockPrice = useMemo(() => {
    const headerPrice = getNumericAny(headerRow, priceFieldCandidates);
    if (isFiniteNumber(headerPrice) && headerPrice > 0) return headerPrice;

    const stockScreenerPrice = getNumericAny(stockScreenerRow, priceFieldCandidates);
    if (isFiniteNumber(stockScreenerPrice) && stockScreenerPrice > 0) {
      return stockScreenerPrice;
    }

    return null;
  }, [headerRow, stockScreenerRow, priceFieldCandidates]);

  const stockPriceSource = useMemo(() => {
    const headerPrice = getNumericAny(headerRow, priceFieldCandidates);
    if (isFiniteNumber(headerPrice) && headerPrice > 0) return "Sheets/API";

    const stockScreenerPrice = getNumericAny(stockScreenerRow, priceFieldCandidates);
    if (isFiniteNumber(stockScreenerPrice) && stockScreenerPrice > 0) {
      return "stock_screener.json";
    }

    return "Unavailable";
  }, [headerRow, stockScreenerRow, priceFieldCandidates]);

  const stockPriceDate = useMemo(() => {
    const raw = getAny(headerRow, headerKeyIndex, [
      "Price Date",
      "As Of",
      "Date",
      "Last Updated",
      "Updated",
      "Timestamp",
      "Quote Date",
    ]);

    const headerDate = normalizeDateString(raw || "");
    if (headerDate) return headerDate;

    return normalizeDateString(stockScreenerAsOf || "");
  }, [headerRow, headerKeyIndex, stockScreenerAsOf]);

  const asymmetryStats = useMemo(() => {
    return computeAsymmetryStats(tickerDailyRows, {
      dropThreshold: -0.03,
      lookaheadDays: 5,
      minEvents: 1,
    });
  }, [tickerDailyRows]);

  const sortedAscRows = useMemo(() => {
    return [...macroRows].sort((a, b) => compareValues(a.date, b.date, "asc"));
  }, [macroRows]);

  const last30Rows = useMemo(() => {
    return sortedAscRows.slice(-30);
  }, [sortedAscRows]);

  const macro3DData = useMemo(() => {
    const validRows = macroRows
      .map((row) => ({
        ...row,
        relative_return_for_sensitivity: isFiniteNumber(row.relative_return)
          ? row.relative_return
          : row.stock_return,
      }))
      .filter(
        (r) =>
          isFiniteNumber(r.inflation_score) &&
          isFiniteNumber(r.growth_score) &&
          isFiniteNumber(r.liquidity_score) &&
          isFiniteNumber(r.relative_return_for_sensitivity)
      );

    if (!validRows.length) {
      return {
        singlePoint: null,
        methodology:
          "Relative-return sensitivity versus SPY. Falls back to stock return when SPY is unavailable.",
      };
    }

    const sensitivityRows = validRows.map((row) => ({
      ...row,
      relative_return: row.relative_return_for_sensitivity,
    }));

    const inflationPreference = computeMacroDimensionPreference(
      sensitivityRows,
      "inflation_score"
    );
    const growthPreference = computeMacroDimensionPreference(
      sensitivityRows,
      "growth_score"
    );
    const liquidityPreference = computeMacroDimensionPreference(
      sensitivityRows,
      "liquidity_score"
    );

    const avgRelativeReturn = averageFinite(
      sensitivityRows.map((row) => row.relative_return)
    );

    const singlePoint = {
      key: "relative_return_sensitivity",
      label: "Relative Return Sensitivity",
      count: sensitivityRows.length,
      avg_relative_return: avgRelativeReturn,
      avg_return: avgRelativeReturn,
      inflationAxis: inflationPreference.score,
      growthAxis: growthPreference.score,
      liquidityAxis: liquidityPreference.score,
      x: inflationPreference.score,
      y: growthPreference.score,
      z: liquidityPreference.score,
      inflationPreference,
      growthPreference,
      liquidityPreference,
    };

    return {
      singlePoint,
      methodology:
        "Axis = high macro tercile average relative return minus low macro tercile average relative return, normalized by relative-return volatility and blended with correlation.",
    };
  }, [macroRows]);


  const macroSummaryRows = useMemo(() => {
    const groups = new Map();

    for (const row of macroRows) {
      const key = row.macro_regime || "—";
      if (!groups.has(key)) {
        groups.set(key, {
          macro_regime: key,
          count: 0,
          sum_return: 0,
          sum_relative: 0,
          num_up: 0,
          num_down: 0,
          count_return: 0,
          count_relative: 0,
        });
      }

      const g = groups.get(key);
      g.count += 1;

      if (isFiniteNumber(row.stock_return)) {
        g.sum_return += row.stock_return;
        g.count_return += 1;
        if (row.stock_return > 0) g.num_up += 1;
        if (row.stock_return < 0) g.num_down += 1;
      }

      if (isFiniteNumber(row.relative_return)) {
        g.sum_relative += row.relative_return;
        g.count_relative += 1;
      }
    }

    return [...groups.values()]
      .map((g) => ({
        macro_regime: g.macro_regime,
        avg_daily_return:
          g.count_return > 0 ? g.sum_return / g.count_return : null,
        num_up: g.num_up,
        num_down: g.num_down,
        avg_return_vs_spy:
          g.count_relative > 0 ? g.sum_relative / g.count_relative : null,
        total_days: g.count,
      }))
      .sort((a, b) => {
        const aVal = isFiniteNumber(a.avg_daily_return)
          ? a.avg_daily_return
          : -999;
        const bVal = isFiniteNumber(b.avg_daily_return)
          ? b.avg_daily_return
          : -999;
        return bVal - aVal;
      });
  }, [macroRows]);

  const regimeProfileRows = useMemo(() => {
    const valid = macroSummaryRows.filter(
      (r) =>
        r.macro_regime &&
        r.macro_regime !== "—" &&
        isFiniteNumber(r.avg_daily_return) &&
        isFiniteNumber(r.total_days) &&
        r.total_days > 0
    );

    if (!valid.length) return [];

    // This is a performance-weighted regime fingerprint.
    // Positive average-return regimes get weight proportional to avg return × sample days.
    // If no regime has positive average return, fall back to absolute return magnitude
    // so the profile still sums to 100 instead of showing all zeros.
    let weighted = valid.map((r) => ({
      ...r,
      profile_weight: Math.max(r.avg_daily_return, 0) * r.total_days,
    }));

    let totalWeight = weighted.reduce((sum, r) => sum + r.profile_weight, 0);

    if (totalWeight <= 0) {
      weighted = valid.map((r) => ({
        ...r,
        profile_weight: Math.abs(r.avg_daily_return) * r.total_days,
      }));
      totalWeight = weighted.reduce((sum, r) => sum + r.profile_weight, 0);
    }

    if (totalWeight <= 0) {
      weighted = valid.map((r) => ({
        ...r,
        profile_weight: r.total_days,
      }));
      totalWeight = weighted.reduce((sum, r) => sum + r.profile_weight, 0);
    }

    return weighted
      .map((r) => ({
        ...r,
        profile_pct: totalWeight > 0 ? (r.profile_weight / totalWeight) * 100 : 0,
      }))
      .sort((a, b) => b.profile_pct - a.profile_pct);
  }, [macroSummaryRows]);

  const bestMacroType = useMemo(() => {
    if (!macroSummaryRows.length) return "—";
    const valid = macroSummaryRows.filter((r) =>
      isFiniteNumber(r.avg_daily_return)
    );
    if (!valid.length) return "—";
    return valid[0]?.macro_regime || "—";
  }, [macroSummaryRows]);

  const dailyReturn30d = useMemo(() => {
    if (!last30Rows.length) return null;
    let product = 1;
    let count = 0;

    for (const r of last30Rows) {
      if (isFiniteNumber(r.stock_return)) {
        product *= 1 + r.stock_return;
        count += 1;
      }
    }

    return count ? product - 1 : null;
  }, [last30Rows]);

  const relativeReturn30d = useMemo(() => {
    if (!last30Rows.length) return null;
    let product = 1;
    let count = 0;

    for (const r of last30Rows) {
      if (isFiniteNumber(r.relative_return)) {
        product *= 1 + r.relative_return;
        count += 1;
      }
    }

    return count ? product - 1 : null;
  }, [last30Rows]);

  const chartRows = useMemo(() => {
    return last30Rows.map((r) => ({
      date: r.date,
      label: formatShortDate(r.date),
      stock_return_pct: isFiniteNumber(r.stock_return)
        ? r.stock_return * 100
        : null,
      relative_return_pct: isFiniteNumber(r.relative_return)
        ? r.relative_return * 100
        : null,
      macro_regime: r.macro_regime,
    }));
  }, [last30Rows]);

  const decompChartRows = useMemo(() => {
    return [...decompRows]
      .map((r) => ({
        date: normalizeDateString(r?.date),
        label: formatShortDate(r?.date),
        total_return_pct: isFiniteNumber(Number(r?.return))
          ? Number(r.return)
          : null,
        macro_pct: isFiniteNumber(Number(r?.macro_component))
          ? Number(r.macro_component)
          : null,
        cluster_pct: isFiniteNumber(Number(r?.cluster_component))
          ? Number(r.cluster_component)
          : null,
        idio_pct: isFiniteNumber(Number(r?.idiosyncratic_component))
          ? Number(r.idiosyncratic_component)
          : null,
      }))
      .filter((r) => r.date)
      .sort((a, b) => compareValues(a.date, b.date, "asc"))
      .slice(-10);
  }, [decompRows]);

  const decompDomain = useMemo(() => {
    const vals = decompChartRows
      .flatMap((r) => [
        r.total_return_pct,
        r.macro_pct,
        r.cluster_pct,
        r.idio_pct,
      ])
      .filter(isFiniteNumber);

    if (!vals.length) return [-2, 2];

    const maxAbs = Math.max(...vals.map((v) => Math.abs(v)));
    const padded = Math.max(0.5, maxAbs * 1.15);
    const rounded = Math.ceil(padded * 2) / 2;

    return [-rounded, rounded];
  }, [decompChartRows]);

  const volatilityStats = useMemo(() => {
    return buildVolatilityStats(tickerDailyRows, stockPrice, stockPriceDate);
  }, [tickerDailyRows, stockPrice, stockPriceDate]);

  const monthlyMacroPriceRows = useMemo(() => {
    return buildMonthlyMacroPriceSeries(
      tickerDailyRows,
      stockPrice,
      stockPriceDate
    );
  }, [tickerDailyRows, stockPrice, stockPriceDate]);

  const googleNewsUrl = `https://news.google.com/search?q=${encodeURIComponent(
    `${sym} stock`
  )}&hl=en-US&gl=US&ceid=US:en`;

  return (
    <div
      style={{
        maxWidth: 1400,
        margin: "0 auto",
        padding: 20,
        fontFamily: "system-ui",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "flex-start",
          gap: 12,
          flexWrap: "wrap",
          marginBottom: 14,
        }}
      >
        <div>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 12,
              flexWrap: "wrap",
            }}
          >
            <h2 style={{ margin: 0, lineHeight: 1.1 }}>{sym || "Stock"}</h2>
            <div
              style={{
                fontSize: 20,
                fontWeight: 700,
                color: "#111827",
              }}
            >
              {formatPrice(stockPrice)}
            </div>
          </div>

          {exParam ? (
            <div style={{ color: "#6b7280", fontSize: 13, marginTop: 4 }}>
              {exParam}
            </div>
          ) : null}

          {headerError ? (
            <div style={{ color: "#9ca3af", fontSize: 12, marginTop: 4 }}>
              {headerError}
            </div>
          ) : null}

          <div style={{ color: "#9ca3af", fontSize: 12, marginTop: 4 }}>
            Price source: {stockPriceSource}
            {stockPriceDate ? ` · ${stockPriceDate}` : ""}
            {stockScreenerError ? ` · stockScreener fallback error: ${stockScreenerError}` : ""}
          </div>
        </div>

        <div
          style={{
            marginLeft: "auto",
            display: "flex",
            gap: 12,
            fontSize: 14,
            flexWrap: "wrap",
          }}
        >
          <Link to="/stocks" style={{ textDecoration: "none" }}>
            Stocks
          </Link>
          <Link to="/etfs" style={{ textDecoration: "none" }}>
            ETFs
          </Link>
          <Link to="/feed" style={{ textDecoration: "none" }}>
            Feed
          </Link>
        </div>
      </div>

      <div
        style={{
          border: "1px solid #eee",
          borderRadius: 14,
          background: "#fff",
          padding: 12,
          marginBottom: 16,
        }}
      >
        <div style={{ fontWeight: 800, fontSize: 16, marginBottom: 10 }}>
          Where {sym} Performs Best Across Macro Dimensions
        </div>

        <div
          style={{
            marginBottom: 12,
            display: "inline-block",
            padding: "6px 10px",
            borderRadius: 9999,
            background: "#f3f4f6",
            color: "#111827",
            fontWeight: 700,
            fontSize: 13,
          }}
        >
          Stock Type: {bestMacroType}
        </div>

        <RegimeProfileBar rows={regimeProfileRows} />

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "minmax(340px, 1fr) minmax(420px, 1fr)",
            gap: 16,
            alignItems: "start",
          }}
        >
          <div>
            {loadingMacroTable ? (
              <div style={{ padding: 18, color: "#666" }}>Loading macro coordinate map…</div>
            ) : !macro3DData.singlePoint ? (
              <div style={{ padding: 18, color: "#666" }}>No macro coordinate data available.</div>
            ) : (
              <MacroCoordinateMap sym={sym} data={macro3DData} />
            )}
          </div>

          <div
            style={{
              border: "1px solid #f3f4f6",
              borderRadius: 12,
              overflow: "hidden",
              background: "#fff",
              minHeight: 340,
            }}
          >
            <div
              style={{
                padding: 10,
                background: "#fafafa",
                borderBottom: "1px solid #eee",
                fontWeight: 800,
                fontSize: 14,
              }}
            >
              Macro Comparison Table
            </div>

            {loadingMacroTable ? (
              <div style={{ padding: 18, color: "#666" }}>
                Loading macro comparison…
              </div>
            ) : macroSummaryRows.length === 0 ? (
              <div style={{ padding: 18, color: "#666" }}>
                No macro comparison data available.
              </div>
            ) : (
              <div style={{ overflowX: "auto", maxHeight: 340 }}>
                <table
                  style={{
                    width: "100%",
                    minWidth: 700,
                    borderCollapse: "collapse",
                  }}
                >
                  <thead
                    style={{ background: "#f9fafb", position: "sticky", top: 0 }}
                  >
                    <tr>
                      <th style={tableThStyle}>Macro Dimension</th>
                      <th style={tableThStyle}>Avg Daily Return</th>
                      <th style={tableThStyle}>Num Up</th>
                      <th style={tableThStyle}>Num Down</th>
                      <th style={tableThStyle}>Avg Return vs SPY</th>
                      <th style={tableThStyle}>Total Days</th>
                    </tr>
                  </thead>
                  <tbody>
                    {macroSummaryRows.map((r) => (
                      <tr key={r.macro_regime}>
                        <td style={tableTdStyle}>
                          <span style={getRegimeStyle(r.macro_regime)}>
                            {r.macro_regime}
                          </span>
                        </td>
                        <td
                          style={{
                            ...tableTdStyle,
                            ...getReturnStyle(r.avg_daily_return),
                          }}
                        >
                          {formatSignedPercentFromDecimal(r.avg_daily_return)}
                        </td>
                        <td style={tableTdStyle}>{r.num_up}</td>
                        <td style={tableTdStyle}>{r.num_down}</td>
                        <td
                          style={{
                            ...tableTdStyle,
                            ...getReturnStyle(r.avg_return_vs_spy),
                          }}
                        >
                          {formatSignedPercentFromDecimal(r.avg_return_vs_spy)}
                        </td>
                        <td style={tableTdStyle}>{r.total_days}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        <FundamentalsSnapshotTable
          sym={sym}
          stockScreenerRow={stockScreenerRow}
          headerRow={headerRow}
          asOf={stockScreenerAsOf}
        />
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
          gap: 16,
          marginBottom: 16,
        }}
      >
        <div
          style={{
            border: "1px solid #eee",
            borderRadius: 14,
            background: "#fff",
            padding: 12,
          }}
        >
          <div style={{ fontWeight: 800, fontSize: 16, marginBottom: 6 }}>
            Last 30 Days: {sym} Daily Return %
          </div>
          <div
            style={{
              fontSize: 13,
              fontWeight: 700,
              color: isFiniteNumber(dailyReturn30d)
                ? dailyReturn30d >= 0
                  ? "#15803d"
                  : "#b91c1c"
                : "#6b7280",
              marginBottom: 10,
            }}
          >
            {isFiniteNumber(dailyReturn30d)
              ? `${dailyReturn30d > 0 ? "+" : ""}${(
                  dailyReturn30d * 100
                ).toFixed(2)}% total return in last 30 days`
              : "—"}
          </div>

          {loadingMacroTable ? (
            <div style={{ padding: 18, color: "#666" }}>Loading chart…</div>
          ) : chartRows.length === 0 ? (
            <div style={{ padding: 18, color: "#666" }}>
              No chart data available.
            </div>
          ) : (
            <div style={{ width: "100%", height: 340 }}>
              <ResponsiveContainer>
                <BarChart
                  data={chartRows}
                  margin={{ top: 10, right: 20, left: 0, bottom: 10 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="label" minTickGap={20} />
                  <YAxis
                    tickFormatter={(v) => `${v.toFixed(1)}%`}
                    domain={["auto", "auto"]}
                  />
                  <Tooltip
                    content={<SingleBarTooltip title={`${sym} Daily Return`} />}
                  />
                  <Legend />
                  <Bar dataKey="stock_return_pct" name={`${sym} Daily Return`}>
                    {chartRows.map((entry, idx) => (
                      <Cell
                        key={`stock-cell-${idx}`}
                        fill={getBarColor(entry.stock_return_pct)}
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>

        <div
          style={{
            border: "1px solid #eee",
            borderRadius: 14,
            background: "#fff",
            padding: 12,
          }}
        >
          <div style={{ fontWeight: 800, fontSize: 16, marginBottom: 6 }}>
            Last 30 Days: {sym} Return vs SPY %
          </div>
          <div
            style={{
              fontSize: 13,
              fontWeight: 700,
              color: isFiniteNumber(relativeReturn30d)
                ? relativeReturn30d >= 0
                  ? "#15803d"
                  : "#b91c1c"
                : "#6b7280",
              marginBottom: 10,
            }}
          >
            {isFiniteNumber(relativeReturn30d)
              ? `${relativeReturn30d > 0 ? "+" : ""}${(
                  relativeReturn30d * 100
                ).toFixed(2)}% return vs SPY in last 30 days`
              : "—"}
          </div>

          {loadingMacroTable ? (
            <div style={{ padding: 18, color: "#666" }}>Loading chart…</div>
          ) : chartRows.length === 0 ? (
            <div style={{ padding: 18, color: "#666" }}>
              No chart data available.
            </div>
          ) : (
            <div style={{ width: "100%", height: 340 }}>
              <ResponsiveContainer>
                <BarChart
                  data={chartRows}
                  margin={{ top: 10, right: 20, left: 0, bottom: 10 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="label" minTickGap={20} />
                  <YAxis
                    tickFormatter={(v) => `${v.toFixed(1)}%`}
                    domain={["auto", "auto"]}
                  />
                  <Tooltip content={<SingleBarTooltip title={`${sym} vs SPY`} />} />
                  <Legend />
                  <Bar dataKey="relative_return_pct" name={`${sym} vs SPY`}>
                    {chartRows.map((entry, idx) => (
                      <Cell
                        key={`rel-cell-${idx}`}
                        fill={getBarColor(entry.relative_return_pct)}
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      </div>

      {loadingMacroTable ? (
        <div
          style={{
            border: "1px solid #eee",
            borderRadius: 14,
            background: "#fff",
            padding: 18,
            color: "#666",
            marginBottom: 16,
          }}
        >
          Loading macro line chart…
        </div>
      ) : macroTableError ? (
        <div
          style={{
            border: "1px solid #fecaca",
            borderRadius: 14,
            background: "#fef2f2",
            padding: 16,
            color: "#b91c1c",
            marginBottom: 16,
          }}
        >
          {macroTableError}
        </div>
      ) : (
        <MacroPerformanceStripChart
          rows={chartRows}
          sym={sym}
          currentPrice={stockPrice}
          currentPriceDate={stockPriceDate}
          tickerDailyRows={tickerDailyRows}
        />
      )}

      <VolatilityRangeSection sym={sym} stats={volatilityStats} />

      <AsymmetrySection
        sym={sym}
        stats={asymmetryStats}
        lookaheadDays={5}
        dropThreshold={-0.03}
      />

      <div
        style={{
          border: "1px solid #eee",
          borderRadius: 14,
          background: "#fff",
          padding: 12,
          marginBottom: 16,
        }}
      >
        <div style={{ fontWeight: 800, fontSize: 16, marginBottom: 6 }}>
          Last 30 Days: Return Decomposition
        </div>
        <div style={{ color: "#6b7280", fontSize: 13, marginBottom: 12 }}>
          Four aligned panels with the same date axis and same y-scale. Black-box
          labels are shown on every point, including total return.
        </div>

        {loadingDecomp ? (
          <div style={{ padding: 18, color: "#666" }}>
            Loading decomposition chart…
          </div>
        ) : decompError ? (
          <div
            style={{
              padding: 16,
              color: "#b91c1c",
              background: "#fef2f2",
              border: "1px solid #fecaca",
              borderRadius: 10,
            }}
          >
            {decompError}
          </div>
        ) : decompChartRows.length === 0 ? (
          <div style={{ padding: 18, color: "#666" }}>
            No decomposition data available for this ticker.
          </div>
        ) : (
          <div
            style={{
              border: "1px solid #f3f4f6",
              borderRadius: 12,
              padding: 10,
              background: "#fff",
            }}
          >
            <DecompositionMiniPanel
              title="Total Return"
              dataKey="total_return_pct"
              color="#111827"
              rows={decompChartRows}
              domain={decompDomain}
              showXAxis={false}
              showDataLabels
            />
            <DecompositionMiniPanel
              title="Macro"
              dataKey="macro_pct"
              color="#4f46e5"
              rows={decompChartRows}
              domain={decompDomain}
              showXAxis={false}
              showDataLabels
            />
            <DecompositionMiniPanel
              title="Sector"
              dataKey="cluster_pct"
              color="#2563eb"
              rows={decompChartRows}
              domain={decompDomain}
              showXAxis={false}
              showDataLabels
            />
            <DecompositionMiniPanel
              title="Idiosyncratic"
              dataKey="idio_pct"
              color="#dc2626"
              rows={decompChartRows}
              domain={decompDomain}
              showXAxis
              showDataLabels
            />
          </div>
        )}
      </div>

      <div
        style={{
          border: "1px solid #eee",
          borderRadius: 14,
          background: "#fff",
          padding: 12,
          marginBottom: 16,
        }}
      >
        <div
          style={{
            display: "flex",
            gap: 10,
            alignItems: "center",
            flexWrap: "wrap",
          }}
        >
          <h3 style={{ margin: 0, fontSize: 15 }}>Recent Articles</h3>
          <a
            href={googleNewsUrl}
            target="_blank"
            rel="noreferrer"
            style={{
              marginLeft: "auto",
              fontSize: 12,
              textDecoration: "none",
              border: "1px solid #eee",
              borderRadius: 999,
              padding: "5px 9px",
              background: "#fafafa",
              color: "#222",
            }}
            title="Open Google News search"
          >
            Open Google News →
          </a>
        </div>

        {loadingNews && (
          <div style={{ marginTop: 8, color: "#666", fontSize: 12 }}>
            Loading articles…
          </div>
        )}

        {!loadingNews && news.length === 0 && (
          <div style={{ marginTop: 8, color: "#666", fontSize: 12 }}>
            No articles loaded.
          </div>
        )}

        {!loadingNews && news.length > 0 && (
          <div
            style={{
              marginTop: 8,
              display: "flex",
              flexDirection: "column",
              gap: 8,
            }}
          >
            {news.slice(0, 5).map((n, idx) => (
              <a
                key={idx}
                href={n.link}
                target="_blank"
                rel="noreferrer"
                style={{
                  textDecoration: "none",
                  color: "inherit",
                  border: "1px solid #eee",
                  borderRadius: 12,
                  padding: 10,
                  background: "#fff",
                  display: "block",
                }}
              >
                <div
                  style={{
                    fontWeight: 850,
                    lineHeight: 1.25,
                    fontSize: 13,
                  }}
                >
                  {n.title}
                </div>

                {(n.source || n.age || n.date) && (
                  <div style={{ marginTop: 5, color: "#777", fontSize: 11 }}>
                    {n.source ? n.source : ""}
                    {n.source && (n.age || n.date) ? " · " : ""}
                    {n.age ? n.age : ""}
                    {n.age && n.date ? " · " : ""}
                    {n.date ? n.date : ""}
                  </div>
                )}

                {n.snippet ? (
                  <div
                    style={{
                      marginTop: 6,
                      color: "#444",
                      fontSize: 12,
                      lineHeight: 1.35,
                    }}
                  >
                    {String(n.snippet).length > 190
                      ? String(n.snippet).slice(0, 190) + "…"
                      : n.snippet}
                  </div>
                ) : null}
              </a>
            ))}
          </div>
        )}
      </div>

      <div
        style={{
          border: "1px solid #eee",
          borderRadius: 14,
          background: "#fff",
          padding: 12,
          marginBottom: 16,
        }}
      >
        <div style={{ fontWeight: 800, fontSize: 16, marginBottom: 6 }}>
          {sym} Stock Cluster
        </div>
        <div style={{ color: "#6b7280", fontSize: 13, marginBottom: 12 }}>
          Other stocks in the same cluster, ranked by their correlation with {sym}.
        </div>

        {loadingCluster ? (
          <div style={{ padding: 18, color: "#666" }}>
            Loading stock cluster…
          </div>
        ) : clusterError ? (
          <div
            style={{
              padding: 16,
              color: "#b91c1c",
              background: "#fef2f2",
              border: "1px solid #fecaca",
              borderRadius: 10,
            }}
          >
            {clusterError}
          </div>
        ) : clusterRows.length === 0 ? (
          <div style={{ padding: 18, color: "#666" }}>
            No cluster data available for this ticker.
          </div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table
              style={{
                width: "100%",
                minWidth: 520,
                borderCollapse: "collapse",
              }}
            >
              <thead style={{ background: "#f9fafb" }}>
                <tr>
                  <th style={tableThStyle}>Ticker</th>
                  <th style={tableThStyle}>Correlation</th>
                </tr>
              </thead>
              <tbody>
                {clusterRows.map((row) => (
                  <tr key={row.ticker}>
                    <td style={tableTdStyle}>
                      <Link
                        to={`/stocks/${encodeURIComponent(row.ticker)}`}
                        style={{ textDecoration: "none" }}
                      >
                        {row.ticker}
                      </Link>
                    </td>
                    <td
                      style={{
                        ...tableTdStyle,
                        ...getReturnStyle(row.correlation),
                      }}
                    >
                      {isFiniteNumber(row.correlation)
                        ? row.correlation.toFixed(3)
                        : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <MonthlyMacroPriceChart
        sym={sym}
        monthlyRows={monthlyMacroPriceRows}
        usingIndexedFallback={!isFiniteNumber(stockPrice)}
      />
    </div>
  );
}